// The API contract shared by the server and the phone app. Zod schemas are the single definition:
// the API validates requests and responses with them, the web app types its client from them.
import { z } from 'zod';
export const Locale = z.enum(['en', 'es']);
export const RoleCode = z.enum(['owner', 'office', 'warehouse', 'driver', 'sales', 'readonly', 'customer']);
export const UnitCode = z.enum(['BX', 'CS', 'SK', 'BG', 'CR', 'BL', 'JR', 'DZ', 'LB', 'EA', 'BN', 'PL']);
/** a unit sold out of a box (decision 166): pounds, each, dozens, bunches; a QuickBooks product sold that way says which */
export const LooseUnit = z.enum(['LB', 'EA', 'DZ', 'BN']);
export const ShelfLifeClass = z.enum(['delicate', 'medium', 'hardy', 'shelf_stable']);
// Tiers are data (any number of them, each a margin): a code is a short lowercase slug.
export const PriceTierCode = z.string().regex(/^[a-z][a-z0-9_]{1,29}$/, 'lowercase slug, 2-30 chars');
export const ContentUnit = z.enum(['lb', 'ct', 'bu', 'oz', 'ea']);
export const Sensitivity = z.enum(['H', 'M', 'L']);
export const ItemStatus = z.enum(['active', 'seasonal', 'dormant']);
export const OpenRemainingUnit = z.enum(['lb', 'ct', 'bu', 'oz', 'ea', 'unit']);
export const OrderChannel = z.enum(['phone', 'sms', 'whatsapp', 'walkin', 'web', 'standing', 'import', 'transfer']);
export const OrderStatus = z.enum(['draft', 'confirmed', 'picked', 'out_for_delivery', 'delivered', 'invoiced', 'cancelled']);
export const Money = z.number().finite().nonnegative();
export const Qty = z.number().finite().positive();
export const Uuid = z.string().uuid();
export const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
export const Phone = z.string().regex(/^\+1\d{10}$/, 'E.164 US number, e.g. +15055551234');
// ---- auth
export const CodeRequest = z.object({ phone: Phone });
export const CodeVerify = z.object({ phone: Phone, code: z.string().regex(/^\d{6}$/) });
export const Me = z.object({
    id: Uuid, name: z.string(), phone: Phone.nullable(), locale: Locale, role: RoleCode, customerId: Uuid.nullable().default(null), homeSiteId: Uuid.nullable().default(null),
    /** false until a password is set: the app nudges the person to set one so they can sign in without a code */
    hasPassword: z.boolean().default(false),
    /** the owner reset the password: the app asks for a new one before anything else */
    mustChange: z.boolean().default(false),
});
export const Password = z.string().min(8, 'at least 8 characters').max(200);
/** A username: a letter first, then letters, digits, dots, dashes or underscores; 2 to 30 long; stored in lower case. Not a secret. */
export const Username = z.string().trim().toLowerCase().regex(/^[a-z][a-z0-9._-]{1,29}$/, 'A username starts with a letter and has 2 to 30 letters, digits, dots, dashes or underscores');
export const PasswordLogin = z.object({ login: z.string().trim().min(2).max(200).describe('phone (10 digits), email, or username'), password: z.string().min(1).max(200) });
export const PasswordChange = z.object({ current: z.string().max(200).optional(), password: Password });
// ---- users: the people who sign in, managed by the owner in the app
export const ManagedUser = z.object({
    id: Uuid, name: z.string(), phone: Phone.nullable(), email: z.string().nullable(), username: z.string().nullable(), locale: Locale, role: RoleCode, active: z.boolean(),
    homeSiteId: Uuid.nullable(), hasPassword: z.boolean(), lastSeenAt: z.string().nullable(),
});
export const UserCreate = z.object({
    name: z.string().trim().min(1).max(120), phone: Phone.optional(), email: z.string().trim().email().max(200).optional(), username: Username.optional(), locale: Locale.default('en'),
    role: RoleCode.exclude(['customer']), homeSiteId: Uuid.optional(), password: Password.optional(),
}).refine((u) => u.phone || u.email || u.username, { message: 'A phone, an email or a username is needed to sign in', path: ['phone'] });
export const UserPatch = z.object({
    name: z.string().trim().min(1).max(120).optional(), phone: Phone.nullable().optional(), email: z.string().trim().email().max(200).nullable().optional(), username: Username.nullable().optional(),
    locale: Locale.optional(), role: RoleCode.exclude(['customer']).optional(), active: z.boolean().optional(), homeSiteId: Uuid.nullable().optional(),
    /** a new password set by the owner; the person is asked to change it at their next sign-in */
    password: Password.optional(),
});
// ---- items
export const Item = z.object({
    id: Uuid,
    name: z.string(),
    category: z.string().nullable(),
    categoryId: Uuid.nullable().default(null),
    homeVendor: z.string().nullable(),
    /** the vendor the order day buys it from (its tab), and whose cost prices it */
    homeVendorId: Uuid.nullable().default(null),
    packUnit: UnitCode,
    packDesc: z.string().nullable(),
    retailUnit: UnitCode.nullable(),
    retailUnitsPerPack: z.number().nullable(),
    sellSplit: z.boolean(),
    shelfLifeClass: ShelfLifeClass,
    keepOnHandPacks: z.number().nullable(),
    /** the cost follows the vendor the item is ordered from (decision 122): vendorName says whose price it is, source which paper */
    cost: z.object({ amount: Money, effectiveDate: IsoDate, vendorCode: z.string().nullable(), vendorName: z.string().nullable().optional(), source: z.string().nullable().optional() }).nullable(),
    /** Price per tier code (every active tier that has a price for the item). */
    prices: z.record(z.string(), Money).default({}),
    active: z.boolean(),
    // catalog (docs/CATALOG.md)
    sku: z.string().nullable(),
    family: z.string().nullable(),
    variety: z.string().nullable(),
    nameEs: z.string().nullable(),
    content: z.number().positive().nullable(),
    contentUnit: ContentUnit.nullable(),
    sellLoose: z.boolean(),
    sellHalf: z.boolean(),
    sensitivity: Sensitivity,
    status: ItemStatus,
    /** Staff-only description (pack notes, what to watch for). Never on an invoice or a price list. */
    internalNote: z.string().nullable(),
    /** the box as bought (decision 138): what a full box weighs and how many pieces it holds */
    boxWeightLb: z.number().nullable().default(null),
    boxCount: z.number().int().nullable().default(null),
    /** where it comes from this month (the sourcing by season), null when not set */
    originNow: z.string().nullable().default(null),
});
/** FAM-VAR-PACK (docs/CATALOG.md): ONI-YEL-50LB, a decimal pack as P (50P5LB), a bare pack unit (TOM-STD-BX), -2 when taken. */
export const Sku = z.string().regex(/^[A-Z0-9]{1,4}-[A-Z0-9]{1,4}-[A-Z0-9]{2,12}(-[0-9]{1,3})?$/, 'FAM-VAR-PACK, e.g. ONI-YEL-50LB');
export const ItemPatch = z.object({
    name: z.string().min(1).optional(),
    shelfLifeClass: ShelfLifeClass.optional(),
    keepOnHandPacks: z.number().nonnegative().nullable().optional(),
    sellSplit: z.boolean().optional(),
    packDesc: z.string().nullable().optional(),
    active: z.boolean().optional(),
    sku: Sku.nullable().optional(),
    family: z.string().min(1).max(60).nullable().optional(),
    variety: z.string().min(1).max(60).nullable().optional(),
    nameEs: z.string().min(1).max(120).nullable().optional(),
    content: z.number().positive().nullable().optional(),
    contentUnit: ContentUnit.nullable().optional(),
    sellLoose: z.boolean().optional(),
    sellHalf: z.boolean().optional(),
    sensitivity: Sensitivity.optional(),
    status: ItemStatus.optional(),
    internalNote: z.string().trim().max(1000).nullable().optional(),
    homeVendorId: Uuid.nullable().optional(),
    /** Fruit, Vegetable, Dry goods...: where it sits on the printed price list (decision 167) */
    categoryId: Uuid.nullable().optional(),
});
/** Administration → Activity (decision 167): who did what, from the audit log every change writes. Owner only. */
export const ActivityArea = z.enum(['sales', 'buying', 'stock', 'prices', 'customers', 'products', 'delivery', 'admin', 'other']);
export const ActivityAction = z.enum(['created', 'changed', 'deleted', 'placed', 'received', 'voided', 'cancelled', 'confirmed', 'picked', 'delivered', 'invoiced', 'closed', 'published', 'counted']);
export const ActivityEntry = z.object({
    id: z.string(),
    at: z.string(),
    /** the person; null = automatic (the QuickBooks copy, a nightly job) */
    who: z.string().nullable(),
    whoId: Uuid.nullable(),
    area: ActivityArea,
    /** the kind of record: the table it lives in (orders, invoices, purchase_orders...) */
    thing: z.string(),
    action: ActivityAction,
    /** which one, in its own words: "#5012 Nelson's Meats $720.00" */
    label: z.string(),
    /** where to see it, when the app has a page for it */
    link: z.string().nullable(),
    /** what changed, for a change (private fields never shown) */
    changes: z.array(z.object({ field: z.string(), from: z.string().nullable(), to: z.string().nullable() })).default([]),
    /** practice money typed while QuickBooks holds the books (decision 140) */
    test: z.boolean().default(false),
});
export const ActivityPage = z.object({
    from: IsoDate, to: IsoDate,
    /** who did something in the period, the most first; id null = automatic */
    people: z.array(z.object({ id: Uuid.nullable(), name: z.string(), count: z.number().int() })),
    entries: z.array(ActivityEntry),
    /** more older entries in the period: ask again with before = next */
    next: z.string().nullable(),
});
/** A product category to pick from (decision 167). */
export const ItemCategory = z.object({ id: Uuid, name: z.string() });
/** A new product (decision 91): the name and how it is bought; the SKU is made by the database, prices by the engine. */
export const ItemCreate = z.object({
    name: z.string().trim().min(1).max(120),
    nameEs: z.string().trim().min(1).max(120).nullable().optional(),
    packUnit: UnitCode.default('CS'),
    packDesc: z.string().trim().max(60).nullable().optional(),
    homeVendorId: Uuid.nullable().optional(),
    family: z.string().trim().min(1).max(60).nullable().optional(),
    variety: z.string().trim().min(1).max(60).nullable().optional(),
    content: z.number().min(0.001).max(999_999).nullable().optional(),
    contentUnit: ContentUnit.nullable().optional(),
    keepOnHandPacks: z.number().nonnegative().max(999_999).nullable().optional(),
    shelfLifeClass: ShelfLifeClass.optional(),
    internalNote: z.string().trim().max(1000).nullable().optional(),
    /** made from a vendor's code not linked yet (Products → Vendor codes, decision 150): linked in the same step */
    fromVendorCode: z.object({ vendorId: Uuid, codeId: Uuid }).optional(),
});
// ---- pricing: tiers, customer rules, the resolved price
export const PriceTier = z.object({ code: PriceTierCode, name: z.string(), /** null for roles that may not see costs */ marginPct: z.number().min(0).max(99.99).nullable(), sort: z.number().int(), active: z.boolean() });
export const PriceTierCreate = z.object({ code: PriceTierCode, name: z.string().min(1).max(60), marginPct: z.number().min(0).max(99.99), sort: z.number().int().optional() });
export const PriceTierPatch = z.object({ name: z.string().min(1).max(60).optional(), marginPct: z.number().min(0).max(99.99).optional(), sort: z.number().int().optional(), active: z.boolean().optional() });
export const RuleKind = z.enum(['margin', 'fixed']);
export const CustomerItemRule = z.object({
    id: Uuid,
    customerId: Uuid,
    itemId: Uuid,
    itemName: z.string(),
    kind: RuleKind,
    marginPct: z.number().nullable(),
    fixedPrice: Money.nullable(),
    startsAt: IsoDate,
    expiresAt: IsoDate.nullable(),
    reason: z.string(),
    setBy: z.string().nullable(),
    createdAt: z.string(),
});
export const CustomerItemRuleCreate = z
    .object({
    itemId: Uuid,
    kind: RuleKind,
    marginPct: z.number().min(0).max(99.99).optional(),
    fixedPrice: Money.optional(),
    startsAt: IsoDate.optional(),
    expiresAt: IsoDate.optional(), // fixed: defaults to settings.pricing.fixed_price_default_days from today
    reason: z.string().trim().min(1).max(500),
    /** the owner sets a rule under the floor or below cost knowingly (decision 181); refused without it */
    approveFloor: z.boolean().optional(),
})
    .refine((r) => (r.kind === 'margin' ? r.marginPct !== undefined && r.fixedPrice === undefined : r.fixedPrice !== undefined && r.marginPct === undefined), {
    message: 'a margin rule carries marginPct, a fixed rule carries fixedPrice',
});
export const PriceSource = z.enum(['rule_fixed', 'rule_margin', 'tier']);
/** The price one customer pays for one item today, with the floor check (cost only for owner and office). */
export const CustomerPrice = z.object({
    itemId: Uuid,
    itemName: z.string(),
    tierCode: PriceTierCode,
    price: Money.nullable(),
    source: PriceSource.nullable(),
    ruleId: Uuid.nullable(),
    ruleExpiresAt: IsoDate.nullable(),
    cost: Money.nullable(),
    floorPrice: Money.nullable(),
    underFloor: z.boolean(),
    belowCost: z.boolean(),
});
/** One item's price in every tier, engine vs published, for the owner's comparison before flipping tier_source. */
export const TierPriceRow = z.object({
    itemId: Uuid,
    itemName: z.string(),
    tierCode: PriceTierCode,
    marginPct: z.number(),
    cost: Money.nullable(),
    price: Money.nullable(),
    source: z.enum(['cost', 'published']).nullable(),
    costPrice: Money.nullable(),
    publishedPrice: Money.nullable(),
});
export const PricingSettings = z.object({
    roundTo: z.number().positive(),
    splitFactor: z.number().positive(),
    splitRoundTo: z.number().positive(),
    holdChangePct: z.number().nonnegative(),
    tierSource: z.enum(['published', 'cost']),
    splitFactorLoose: z.number().positive(),
    looseRoundTo: z.number().positive(),
    fixedPriceDefaultDays: z.number().int().positive(),
    /** the minimum margin by sensitivity, as a fraction (0.14 = 14%): 0 to 0.9 (1 would divide by zero in the engine) */
    floors: z.object({ H: z.number().min(0).max(0.9), M: z.number().min(0).max(0.9), L: z.number().min(0).max(0.9) }),
    openUnitTolerance: z.number().nonnegative(),
});
export const PricingSettingsPatch = PricingSettings.partial();
// ---- the products page: what one item carries beyond the Item row
export const ItemAlias = z.object({ id: Uuid, alias: z.string(), source: z.string() });
export const ItemAliasCreate = z.object({ alias: z.string().trim().min(1).max(120), source: z.enum(['manual', 'qbo', 'clover', 'phone']).default('manual') });
export const ItemVendorCode = z.object({ id: Uuid.optional(), vendorId: Uuid, vendor: z.string(), vendorCode: z.string(), vendorDesc: z.string().nullable(), isPrimary: z.boolean(), cost: Money.nullable(), costDate: IsoDate.nullable() });
export const ItemRule = z.object({ id: Uuid, customerId: Uuid, customerName: z.string(), kind: RuleKind, marginPct: z.number().nullable(), fixedPrice: Money.nullable(), expiresAt: IsoDate.nullable(), reason: z.string() });
export const ItemStock = z.object({
    onHandPacks: z.number(),
    lastCount: z
        .object({ countedAt: z.string(), countedPacks: z.number(), systemPacks: z.number().nullable(), sealed: z.number().nullable(), openRemaining: z.number().nullable(), openRemainingUnit: OpenRemainingUnit.nullable() })
        .nullable(),
    movedLast28Days: z.number(),
});
// the vendors' codes and the products they are linked to, managed on Products → Vendor codes (decision 150)
export const VendorCodeRow = z.object({
    id: Uuid, vendorCode: z.string(), vendorDesc: z.string().nullable(),
    itemId: Uuid.nullable(), itemName: z.string().nullable(), itemSku: z.string().nullable(),
    packUnit: z.string().nullable(), isPrimary: z.boolean(), hidden: z.boolean(),
    cost: z.object({ amount: z.number(), effectiveDate: z.string() }).nullable(),
    /** the newest paper or bill the code was on, and on how many */
    lastSeen: IsoDate.nullable(), papers: z.number().int(),
});
export const VendorCodeChange = z.object({
    /** the product the code is linked to; null unlinks it (it goes back to Unknown names) */
    itemId: Uuid.nullable().optional(),
    /** hidden: a code that is not a product (freight, a fee); it leaves Unknown names */
    hidden: z.boolean().optional(),
    /** the product the screen showed the code linked to: a code changed meanwhile by someone else is refused, not overwritten */
    from: Uuid.nullable().optional(),
})
    .refine((b) => b.itemId !== undefined || b.hidden !== undefined, 'Nothing to change')
    .refine((b) => !(b.hidden === true && b.itemId), 'A code is hidden or linked, not both');
export const VendorCodeCreate = z.object({ vendorCode: z.string().trim().min(1).max(60), vendorDesc: z.string().trim().max(200).optional(), itemId: Uuid });
export const VendorCodeChanged = z.object({ code: VendorCodeRow, costsAdded: z.number().int(), linesMoved: z.number().int() });
export const ItemCatalog = z.object({ aliases: z.array(ItemAlias), vendorCodes: z.array(ItemVendorCode), rules: z.array(ItemRule), stock: ItemStock });
// ---- the unknown-names inbox
export const UnknownNameSource = z.enum(['invoice', 'clover', 'vendor']);
export const UnknownName = z.object({ source: UnknownNameSource, name: z.string(), lineCount: z.number().int(), /** a refund or credit line can make the total negative */ amount: z.number().finite().nullable(), lastSeen: IsoDate.nullable() });
export const UnknownNameAssign = z.object({ source: UnknownNameSource, name: z.string().min(1), itemId: Uuid });
export const UnknownNameAssigned = z.object({ source: UnknownNameSource, name: z.string(), itemId: Uuid, linesAssigned: z.number().int(), aliasAdded: z.boolean(), /** prices from earlier documents that reached the cost ledger now */ costsAdded: z.number().int().default(0) });
/** Names sent to be matched (a chunk of the inbox at a time, so each answer comes back in under a minute). */
export const UnknownNameSuggest = z.object({ names: z.array(z.object({ source: UnknownNameSource, name: z.string().min(1) })).min(1).max(80), ai: z.boolean().default(true) });
/**
 * A product proposed for an unknown name. "sure": the same words once units and counts are set aside, or Claude is
 * sure; "likely": Claude's best reading. Nothing is linked until a person accepts it (AI proposes, people decide).
 */
export const UnknownNameSuggestion = z.object({ source: UnknownNameSource, name: z.string(), itemId: Uuid, itemName: z.string(), confidence: z.enum(['sure', 'likely']), by: z.enum(['words', 'claude']) });
export const UnknownNameSuggestions = z.object({ suggestions: z.array(UnknownNameSuggestion), aiUsed: z.boolean(), /** Claude's part failed: why; the matches by words still came back */ aiError: z.string().nullable().default(null) });
// ---- customers
export const CustomerStage = z.enum(['prospect', 'trial', 'customer', 'lost']);
export const Contact = z.object({
    id: Uuid, name: z.string().nullable(), role: z.string().nullable(), phone: Phone.nullable(),
    whatsapp: z.boolean(), smsConsent: z.boolean(), preferred: z.boolean(),
    birthday: IsoDate.nullable(), // year 1900 = day and month only
    likes: z.string().nullable(),
    portalAccess: z.boolean(), // may sign in to the customer portal with this phone
    email: z.string().nullable().default(null),
    /** invoices and statements are emailed to this contact (accounts payable) */
    getsInvoices: z.boolean().default(false),
});
export const ContactCreate = z.object({
    name: z.string().trim().min(1).max(80),
    role: z.string().trim().max(40).optional(),
    phone: Phone.optional(),
    whatsapp: z.boolean().default(false),
    smsConsent: z.boolean().default(false),
    consentNote: z.string().max(200).optional(),
    preferred: z.boolean().default(false),
    birthday: IsoDate.optional(),
    likes: z.string().max(500).optional(),
    email: z.string().trim().toLowerCase().email().max(200).optional(),
    getsInvoices: z.boolean().default(false),
});
export const ContactPatch = ContactCreate.partial().extend({ phone: Phone.nullable().optional(), birthday: IsoDate.nullable().optional(), likes: z.string().max(500).nullable().optional(), portalAccess: z.boolean().optional(), email: z.string().trim().toLowerCase().email().max(200).nullable().optional() });
// ---- messages (docs/VISION.md row 8): texts in and out, with consent
export const MessageKind = z.enum(['code', 'reply', 'order_confirmation', 'statement', 'promo', 'inbound', 'eta', 'other']);
export const Message = z.object({
    id: Uuid, direction: z.enum(['in', 'out']), channel: z.string(), phone: Phone, customerId: Uuid.nullable(), customerName: z.string().nullable(),
    contactId: Uuid.nullable(), contactName: z.string().nullable(), body: z.string().nullable(), status: z.string(), kind: MessageKind,
    parsedOrderId: Uuid.nullable(), handledAt: z.string().nullable(), error: z.string().nullable(), byName: z.string().nullable(), createdAt: z.string(),
});
export const MessageSend = z.object({ contactId: Uuid, body: z.string().trim().min(1).max(1000), inReplyTo: Uuid.optional() });
export const AudienceMember = z.object({ contactId: Uuid, contactName: z.string().nullable(), customerId: Uuid, customerName: z.string(), language: Locale, phone: Phone, lastQty: Qty.nullable(), lastDate: IsoDate.nullable(), price: Money.nullable() });
export const BlastRequest = z.object({
    contactIds: z.array(Uuid).min(1).max(200),
    itemId: Uuid.optional(), // fills {item}, {price}, {lastQty}
    bodyEn: z.string().trim().min(1).max(1000),
    bodyEs: z.string().trim().min(1).max(1000),
});
export const BlastPreview = z.array(z.object({ contactId: Uuid, contactName: z.string().nullable(), customerName: z.string(), language: Locale, text: z.string() }));
export const BlastResult = z.object({ batchId: Uuid, sent: z.number().int(), skipped: z.array(z.object({ contactId: Uuid, reason: z.string() })) });
export const MessagingStatus = z.object({ configured: z.boolean(), from: z.string().nullable(), reader: z.boolean() });
// ---- routes (docs/VISION.md row 11): the day's deliveries in stop order, proof of delivery, shorts
export const RouteStatus = z.enum(['planned', 'out', 'done']);
export const CollectedMethod = z.enum(['cash', 'check', 'card', 'zelle', 'other']);
export const RouteCash = z.object({
    collected: Money, cash: Money, checks: Money, other: Money, stops: z.number().int(),
    turnedInAt: z.string().nullable(), turnedInBy: z.string().nullable(), turnedInAmount: Money.nullable(), turnedInNote: z.string().nullable(),
});
export const RouteCashTurnIn = z.object({ amount: Money, note: z.string().trim().max(300).optional() });
/** why a line went short at the door (decision 104) */
export const ShortReason = z.enum(['damaged', 'refused', 'not_loaded', 'out_of_stock', 'wrong_item', 'other']);
export const RouteStop = z.object({
    id: Uuid, seq: z.number().int(), orderId: Uuid, orderNo: z.number().int(), customerId: Uuid, customerName: z.string(), orderStatus: OrderStatus,
    address: z.string().nullable(), deliveryNotes: z.string().nullable(), window: z.string().nullable(), phone: Phone.nullable(), callAhead: z.boolean(),
    lines: z.array(z.object({ id: Uuid, itemName: z.string(), qty: Qty, unit: UnitCode, qtyDelivered: z.number().nonnegative().nullable(), shortReason: ShortReason.nullable().default(null), /** the line's price, to work out what to collect after shorts (decision 182) */ unitPrice: z.number().nullable().default(null) })), total: Money,
    arrivedAt: z.string().nullable(), deliveredAt: z.string().nullable(), receivedBy: z.string().nullable(), note: z.string().nullable(), hasPhoto: z.boolean(), hasSignature: z.boolean().default(false), etaTextedAt: z.string().nullable(),
    /** kind: a payment, or a door collection while QuickBooks holds the payments (decision 126) */
    collected: z.object({ amount: Money, method: z.string(), reference: z.string().nullable(), kind: z.enum(['payment', 'door']).default('payment') }).nullable().default(null),
    /** what the customer owes now (the statement's balance) and how much of it is past due; null until the ledger starts */
    balance: z.number().nullable().default(null), pastDue: z.number().default(0),
    /** cash on delivery: terms of 0 days, the driver collects the invoice at the door */
    cod: z.boolean().default(false),
    /** the issued invoice of this stop's order, to print it with the route */
    invoiceId: Uuid.nullable().default(null),
    /** that invoice's total and what is still owed on it: what the driver collects at the door (decision 182) */
    invoiceTotal: z.number().nullable().default(null), invoiceBalance: z.number().nullable().default(null),
    /** the driver could not deliver it, and why (decision 182) */
    notDeliveredAt: z.string().nullable().default(null), notDeliveredReason: z.enum(['closed', 'refused', 'nobody', 'not_reached', 'cancelled', 'other']).nullable().default(null), notDeliveredNote: z.string().nullable().default(null),
});
export const NotDeliveredReason = z.enum(['closed', 'refused', 'nobody', 'other']);
export const StopNotDelivered = z.object({ reason: NotDeliveredReason, note: z.string().trim().max(500).optional() });
/** who can drive a route, for the office's picker (decision 182) */
export const RouteDriver = z.object({ id: Uuid, name: z.string(), role: RoleCode });
/** the office gives a stop the driver could not deliver its next day (decision 182) */
export const StopAnotherDay = z.object({ date: IsoDate });
export const Route = z.object({
    id: Uuid, date: IsoDate, name: z.string(), siteId: Uuid.nullable().default(null), driverId: Uuid.nullable(), driverName: z.string().nullable(), truck: z.string().nullable(), status: RouteStatus,
    startedAt: z.string().nullable(), finishedAt: z.string().nullable(), stops: z.array(RouteStop),
    cash: RouteCash.nullable().default(null),
});
export const StopDeliver = z.object({
    receivedBy: z.string().trim().max(80).optional(),
    /** the signature drawn at the door, as a PNG data URL (kept in the private bucket, printed on the invoice) */
    signaturePng: z.string().regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/).max(400_000).optional(),
    note: z.string().trim().max(500).optional(),
    lines: z.array(z.object({ lineId: Uuid, qtyDelivered: z.number().nonnegative(), shortReason: ShortReason.optional() })).optional(),
    /** money taken at the door: lands as an on-account payment of the customer, tied to this stop */
    collected: z.object({ amount: z.number().positive().multipleOf(0.01), method: CollectedMethod, reference: z.string().trim().max(120).optional() }).optional(),
    /** when it happened at the door, for a delivery kept on the phone with no signal and sent later (decision 109) */
    deliveredAt: z.string().datetime({ offset: true }).optional(),
});
/** The answer to a delivery of an invoiced order already delivered: a replay from the phone takes it as done. */
export const INVOICED_DELIVERED = 'This order was delivered and is invoiced: the office records any change on the invoice';
export const RoutePatch = z.object({ driverId: Uuid.nullable().optional(), truck: z.string().trim().max(40).nullable().optional(), stopOrder: z.array(Uuid).optional() });
/** another truck for the day (decision 104) */
export const RouteCreate = z.object({ date: IsoDate, siteId: Uuid.optional(), name: z.string().trim().min(1).max(40) });
/** a stop moved to another of the day's routes, at its end */
export const StopMove = z.object({ routeId: Uuid });
// ---- the customer portal (docs/VISION.md row 8b): what a customer sees of their own business with us
export const PortalHome = z.object({
    customer: z.object({ id: Uuid, name: z.string(), deliveryDays: z.array(z.number().int()), termsDays: z.number().int(), language: Locale }),
    balance: z.number().nullable(),
    openInvoices: z.number().int(),
    nextDeliveryDate: IsoDate,
    /** the order cut-off (Denver, the day before delivery) and the first day still open to order or cancel (decision 110) */
    cutoff: z.string().default('14:00'),
    firstOpenDay: IsoDate.optional(),
    lastOrder: z.object({ id: Uuid, orderNo: z.number().int(), requestedDate: IsoDate, status: z.string(), total: Money, lines: z.array(z.object({ itemId: Uuid, itemName: z.string(), qty: Qty, unit: UnitCode })) }).nullable(),
});
/** The portal's order cut-off (decision 110): HH:MM, Denver time, the day before delivery. */
export const PortalSettings = z.object({ cutoff: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM, 24-hour') });
/** An invoice on the portal's list: what the customer needs to find it and open it. */
export const PortalInvoiceRow = z.object({ id: Uuid, invoiceNo: z.number().int(), invoiceDate: IsoDate, dueDate: IsoDate.nullable(), total: Money, balance: z.number().nullable() });
export const PortalCatalogItem = z.object({
    itemId: Uuid, name: z.string(), nameEs: z.string().nullable(), packUnit: UnitCode, packDesc: z.string().nullable(),
    price: Money.nullable(), // null = ask: the office prices it on the draft
    lastQty: Qty.nullable(), // what they took last time
});
export const PortalOrderCreate = z.object({
    requestedDate: IsoDate,
    notes: z.string().max(1000).optional(),
    lines: z.array(z.object({ itemId: Uuid, qty: Qty, note: z.string().max(200).optional() })).min(1).max(200),
});
export const CustomerSummary = z.object({
    id: Uuid, name: z.string(), shortName: z.string().nullable(), customerType: z.string().nullable(),
    priceTier: PriceTierCode, callAhead: z.boolean(), deliveryDays: z.array(z.number().int().min(1).max(7)),
    balance: z.number().nullable(), // null until payments are synced from QuickBooks: an unknown balance is never shown as a number
    active: z.boolean(),
    /** put away (decision 184): off the lists, pickers and new orders, history kept */
    archived: z.boolean().default(false),
    stage: CustomerStage,
    creditLimit: Money.nullable().default(null),
    onHold: z.boolean().default(false),
    /** a column of its own on the order day */
    keyAccount: z.boolean().default(false),
});
/** Where the truck goes: the default delivery location with the driver's instructions. */
export const CustomerLocation = z.object({
    addressLine1: z.string().nullable(), addressLine2: z.string().nullable(), city: z.string().nullable(), state: z.string().nullable(), zip: z.string().nullable(),
    deliveryNotes: z.string().nullable(), windowStart: z.string().nullable(), windowEnd: z.string().nullable(),
    /** the route Build puts this customer's orders on (Route 1 when none) */
    routeName: z.string().nullable().default(null),
});
const Hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');
export const CustomerLocationPut = z.object({
    addressLine1: z.string().trim().max(120).nullable().optional(), addressLine2: z.string().trim().max(120).nullable().optional(), city: z.string().trim().max(80).nullable().optional(),
    state: z.string().trim().max(2).nullable().optional(), zip: z.string().trim().max(10).nullable().optional(),
    deliveryNotes: z.string().trim().max(500).nullable().optional(), windowStart: Hhmm.nullable().optional(), windowEnd: Hhmm.nullable().optional(),
    routeName: z.string().trim().max(40).nullable().optional(),
});
export const Customer = CustomerSummary.extend({
    termsDays: z.number().int(),
    language: Locale,
    notes: z.string().nullable(),
    source: z.string().nullable(),
    holdReason: z.string().nullable().default(null),
    keyAccountSort: z.number().int().optional(),
    /** the loads (ISO weekdays) its order day column shows on; null = every load */
    keyAccountLoads: z.array(z.number().int().min(1).max(7)).nullable().optional(),
    termsAgreedOn: IsoDate.nullable().default(null),
    location: CustomerLocation.nullable().default(null),
    taxable: z.boolean().optional(),
    nttc: z.string().nullable().optional(),
    contacts: z.array(Contact),
    recentInvoices: z.array(z.object({ id: Uuid, invoiceNo: z.number().int(), date: IsoDate, total: Money, balance: z.number().nullable() })),
});
export const CustomerCreate = z.object({
    keyAccount: z.boolean().optional(),
    keyAccountSort: z.number().int().min(0).max(1000).optional(),
    /** the loads its order day column shows on; null = every load */
    keyAccountLoads: z.array(z.number().int().min(1).max(7)).max(7).nullable().optional(),
    name: z.string().min(1),
    shortName: z.string().optional(),
    customerType: z.string().optional(),
    priceTier: PriceTierCode.default('standard'),
    callAhead: z.boolean().default(false),
    deliveryDays: z.array(z.number().int().min(1).max(7)).default([]),
    /** PACA: 30 days at most, and only by written agreement; beyond that the trust protection is lost */
    termsDays: z.number().int().min(0).max(30, 'PACA allows 30 days at most, by written agreement').default(0),
    termsAgreedOn: IsoDate.nullable().optional(),
    language: Locale.default('en'),
    notes: z.string().optional(),
    stage: CustomerStage.default('customer'),
    source: z.string().max(200).optional(),
    creditLimit: Money.nullable().optional(),
    onHold: z.boolean().optional(),
    holdReason: z.string().max(300).nullable().optional(),
    taxable: z.boolean().optional(),
    nttc: z.string().trim().max(80).nullable().optional(),
});
export const CustomerPatch = CustomerCreate.partial();
/** Vendors on their own screen (decision 184): who we buy from, how to reach them, their terms and delivery days. */
export const Vendor = z.object({
    id: Uuid, name: z.string(), kind: z.enum(['wholesaler', 'grower', 'retail', 'other']), phone: z.string().nullable(), email: z.string().nullable(),
    termsDays: z.number().int().nullable(), deliveryDays: z.array(z.number().int().min(1).max(7)), notes: z.string().nullable(), archived: z.boolean(),
});
export const VendorCreate = z.object({
    name: z.string().trim().min(1).max(120),
    kind: z.enum(['wholesaler', 'grower', 'retail', 'other']).default('wholesaler'),
    phone: z.string().trim().max(40).nullable().optional(),
    email: z.string().trim().email().max(200).nullable().optional(),
    termsDays: z.number().int().min(0).max(365).nullable().optional(),
    deliveryDays: z.array(z.number().int().min(1).max(7)).max(7).default([]),
    notes: z.string().trim().max(2000).nullable().optional(),
});
export const VendorPatch = VendorCreate.partial();
export const TaxSettings = z.object({ rate: z.number().min(0).max(0.25), name: z.string().trim().min(1).max(40) });
// ---- PACA (decision 57)
/** The seller's letterhead printed on every invoice (owner sets it; nothing in the code): blank fields are left off. */
export const CompanySettings = z.object({
    name: z.string().trim().max(120), addressLine1: z.string().trim().max(120), addressLine2: z.string().trim().max(120),
    city: z.string().trim().max(80), state: z.string().trim().max(40), zip: z.string().trim().max(20),
    phone: z.string().trim().max(40), email: z.union([z.literal(''), z.string().trim().email().max(120)]),
});
export const PacaSettings = z.object({ licenseNo: z.string().trim().max(40).nullable(), interestPct: z.number().min(0).max(36), attorneyFees: z.boolean() });
export const PacaLetterInvoice = z.object({ id: Uuid, invoiceNo: z.number().int(), date: IsoDate, dueDate: IsoDate.nullable(), total: Money, balance: Money, daysLate: z.number().int(), interest: Money, /** days left to give the written trust notice, when the invoice's own notice is the only protection */ noticeDaysLeft: z.number().int() });
export const PacaLetter = z.object({
    customerId: Uuid, customerName: z.string(), customerAddress: z.string().nullable().default(null), asOf: IsoDate, licenseNo: z.string().nullable(), interestPct: z.number(), attorneyFees: z.boolean(), termsDays: z.number().int(), termsAgreedOn: IsoDate.nullable(),
    invoices: z.array(PacaLetterInvoice), principal: Money, interest: Money, total: Money, payBy: IsoDate,
    notices: z.array(z.object({ id: Uuid, kind: z.enum(['demand', 'trust_notice']), sentOn: IsoDate, principal: Money, interest: Money, invoiceCount: z.number().int(), byName: z.string().nullable() })),
});
export const PacaNoticeCreate = z.object({ kind: z.enum(['demand', 'trust_notice']).default('demand'), body: z.string().min(1).max(20000) });
export const PacaNotice = z.object({ id: Uuid, customerId: Uuid, kind: z.enum(['demand', 'trust_notice']), sentOn: IsoDate, principal: Money, interest: Money, invoiceIds: z.array(Uuid) });
export const TaxReport = z.object({
    from: IsoDate, to: IsoDate, rate: z.number(), name: z.string(),
    taxableSales: Money, nontaxableSales: Money, taxCollected: Money,
    byCustomer: z.array(z.object({ customerId: Uuid, customerName: z.string(), taxable: z.boolean(), nttc: z.string().nullable(), sales: Money, tax: Money })),
});
export const AgingBuckets = z.object({ current: Money, d1to30: Money, d31to60: Money, d61to90: Money, d90plus: Money });
export const AgingRow = z.object({
    customerId: Uuid, customerName: z.string(), balance: Money, buckets: AgingBuckets, creditLimit: Money.nullable(), onHold: z.boolean(), oldestDaysLate: z.number().int(), termsDays: z.number().int(),
    /** PACA: when the last demand letter went out, and whether one is due (an invoice past due by more than 10 days with no letter since its due date) */
    lastNoticeOn: IsoDate.nullable().default(null), noticeDue: z.boolean().default(false),
    /** money paid or credited that no open invoice holds (decision 70): on-account payments, general credits, overpayments */
    unapplied: z.number().finite().default(0),
    /** the opening balance at the ledger start (decision 74), part of balance and aged by its due date */
    opening: z.number().finite().default(0),
    /** what they owe: open invoices less the unapplied money; below zero when they paid ahead (the statement's balance) */
    netBalance: z.number().finite(),
});
export const AgingReport = z.object({
    asOf: IsoDate, ledgerStarted: z.boolean(), rows: z.array(AgingRow),
    /** pastDue: what is past due once unapplied money settles the oldest invoices first, the dashboard's number (decision 73) */
    totals: AgingBuckets.extend({ balance: Money, unapplied: z.number().finite(), netBalance: z.number().finite(), pastDue: z.number().finite().default(0) }),
});
/** Per customer: sales and margin over the last 30 days (and the 30 before), 90-day sales, the last invoice and what is past due (decision 68). */
export const CustomerMetricsRow = z.object({
    customerId: Uuid,
    /** sales and margin can be negative (a return, a sale below cost): never refused */
    sales30: z.number(), salesPrev30: z.number(), sales90: z.number(),
    /** margin on the lines with a known cost, and its percent of those lines; null when no line has a cost */
    margin30: z.number().nullable(), marginPct30: z.number().nullable(),
    /** sales over 30 days on lines with a known cost: the base of the margin percent, so a sheet can total it */
    costedSales30: z.number(),
    invoices30: z.number().int(),
    lastInvoiceOn: IsoDate.nullable(),
    /** what is past due after money not tied to an invoice (on-account payments, general credits) settles the oldest
     *  invoices first, and how late the oldest unsettled one is (0 when nothing is past due); null until the ledger is started */
    pastDue: Money.nullable(), oldestDaysLate: z.number().int().nullable(),
});
export const CustomerMetrics = z.object({ asOf: IsoDate, rows: z.array(CustomerMetricsRow) });
// ---- CRM (docs/VISION.md row 10): what happened with a customer, and what is due
export const ActivityKind = z.enum(['visit', 'call', 'text', 'note', 'offer', 'task', 'complaint']);
export const Activity = z.object({
    id: Uuid, customerId: Uuid, kind: ActivityKind, body: z.string(), happenedAt: z.string(),
    dueOn: IsoDate.nullable(), doneAt: z.string().nullable(), byName: z.string().nullable(),
});
export const ActivityCreate = z.object({ kind: ActivityKind, body: z.string().trim().min(1).max(2000), happenedAt: z.string().datetime({ offset: true }).optional(), dueOn: IsoDate.optional() });
export const ActivityPatch = z.object({ done: z.boolean() });
export const Followups = z.object({
    date: IsoDate,
    tasks: z.array(Activity.extend({ customerName: z.string() })),
    birthdays: z.array(z.object({ customerId: Uuid, customerName: z.string(), contactId: Uuid, contactName: z.string().nullable(), birthday: IsoDate, inDays: z.number().int() })),
    quiet: z.array(z.object({ customerId: Uuid, customerName: z.string(), lastOrderDate: IsoDate, daysSince: z.number().int(), usualGapDays: z.number().int() })),
    prospects: z.array(z.object({ customerId: Uuid, customerName: z.string(), stage: CustomerStage, lastTouch: z.string().nullable(), daysSince: z.number().int().nullable() })),
});
/** P2: how each customer is ordering, from the invoices (QuickBooks' while it holds the books; never test ones). */
export const CrmTrendKind = z.enum(['more', 'steady', 'less', 'quiet', 'new']);
export const CrmTrend = z.object({
    customerId: Uuid, customerName: z.string(), stage: CustomerStage,
    trend: CrmTrendKind,
    /** invoiced in the last 4 weeks, and the 4-week average of the 8 weeks before */
    last4: z.number().finite(), prior4: z.number().finite(),
    /** last4 against prior4 in percent; null when there was nothing before */
    changePct: z.number().nullable(),
    lastOrderDate: IsoDate.nullable(), daysSince: z.number().int().nullable(),
    /** the weekdays they usually order on (ISO, 1 = Monday) and the usual days between orders, from the last 6 months */
    usualDays: z.array(z.number().int().min(1).max(7)), usualGapDays: z.number().int().nullable(),
});
export const CrmTrends = z.object({ date: IsoDate, rows: z.array(CrmTrend) });
/** P3: what every customer is likely to order in the days ahead, from the invoices (decision 143). */
export const ForecastAhead = z.object({
    from: IsoDate, to: IsoDate, days: z.array(IsoDate),
    customers: z.array(z.object({
        customerId: Uuid, customerName: z.string(), trend: CrmTrendKind.nullable(),
        /** per day of the range: the chance they order (0..1), what such an order usually comes to, and what is already in */
        days: z.array(z.object({ date: IsoDate, chance: z.number(), usual: z.number().nullable(), booked: z.number().nullable() })),
        /** the dollars to expect over the range: booked days as booked, the rest chance x usual */
        expected: z.number(),
        /** what they usually take in one order, in the product's pack */
        items: z.array(z.object({ itemId: Uuid, itemName: z.string(), unit: z.string(), qty: z.number() })),
        /** the last day they were invoiced, and the weekdays they order on half the weeks or more (1 Monday .. 7 Sunday) */
        lastOrderDate: IsoDate.nullable().default(null),
        usualDays: z.array(z.number().int().min(1).max(7)).default([]),
        /** the By day tab's call list (decision 168), only when asked with contacts=true: the number to call (the preferred
         *  contact's) and the last day someone called, texted or visited them */
        phone: z.string().nullable().default(null),
        contactName: z.string().nullable().default(null),
        calledOn: IsoDate.nullable().default(null),
    })),
    /** per product: the packs to expect each day (chance x usual quantity, summed over customers) and over the range */
    products: z.array(z.object({ itemId: Uuid, itemName: z.string(), unit: z.string(), days: z.array(z.number()), total: z.number() })),
    /** the same model run on each of the last days as if it were that morning, against what was invoiced */
    accuracy: z.object({
        days: z.number().int(),
        /** customers given an even chance or better, how many of them ordered, and how many ordered in all */
        expected: z.number().int(), hits: z.number().int(), ordered: z.number().int(),
        rightPct: z.number().nullable(), caughtPct: z.number().nullable(),
        /** products: how far the packs were off, as a share of what sold (lower is better) */
        productErrorPct: z.number().nullable(),
        dollarsExpected: z.number(), dollarsActual: z.number(),
    }),
});
// ---- orders
export const OrderLineCreate = z.object({
    itemId: Uuid, qty: Qty, unit: UnitCode.default('CS'), note: z.string().max(200).optional(),
    /** a rate typed by the owner or the office instead of the engine's; under the floor or under cost needs the owner's approveFloor */
    unitPrice: Money.optional(),
});
export const OrderCreate = z.object({
    customerId: Uuid,
    requestedDate: IsoDate,
    channel: OrderChannel,
    promised: z.boolean().default(false),
    notes: z.string().max(1000).optional(),
    lines: z.array(OrderLineCreate).min(1).max(200),
    approveFloor: z.boolean().optional(), // owner's tap on a line under its floor
    overrideHold: z.boolean().optional(), // owner's tap past a hold or a credit limit
    siteId: Uuid.optional(), // the site that fills it (default: the hub)
});
export const OrderLine = OrderLineCreate.extend({ id: Uuid, lineNo: z.number().int(), itemName: z.string(), unitPrice: Money.nullable(), qtyDelivered: z.number().nonnegative().nullable().default(null) });
export const Order = z.object({
    id: Uuid, orderNo: z.number().int(), customerId: Uuid, customerName: z.string(), channel: OrderChannel, siteId: Uuid.nullable().default(null),
    status: OrderStatus, requestedDate: IsoDate, promised: z.boolean(), notes: z.string().nullable(),
    lines: z.array(OrderLine), total: Money, createdAt: z.string(),
    /** the order as it stands (one order read alone): sent back with a change to it, so one made meanwhile is caught */
    version: z.string().optional(),
});
/**
 * An order changed before it is invoiced (C6): its lines as they should now read (a line naming the order's own line by
 * lineNo is that line, changed in place; one without is added; an order line not named comes off), the delivery date and
 * the notes. `version` is the order as the screen saw it (GET /orders/:id): a change made meanwhile is refused.
 */
export const OrderRevise = z.object({
    version: z.string().max(64),
    requestedDate: IsoDate.optional(),
    notes: z.string().max(1000).nullable().optional(),
    lines: z.array(OrderLineCreate.extend({ lineNo: z.number().int().positive().optional() })).min(1).max(200),
    approveFloor: z.boolean().optional(),
    overrideHold: z.boolean().optional(),
});
/** The prices an order would be charged, before it is saved (New order shows the server's, C6). */
export const OrderQuoteAsk = z.object({ customerId: Uuid, lines: z.array(z.object({ itemId: Uuid, unit: UnitCode.default('CS') })).max(200) });
export const OrderQuote = z.object({
    lines: z.array(z.object({
        unitPrice: Money.nullable(),
        /** under its floor or below cost at this price: saving it needs the owner's approval */
        needsOwner: z.boolean(),
    })),
});
export const OrderStatusPatch = z.object({
    status: OrderStatus,
    reason: z.string().max(500).optional(),
    /** owner only: confirm a draft even though the customer is on hold or over the limit (decision 108) */
    overrideHold: z.boolean().optional(),
});
// ---- invoicing: invoice from an order, payments, credits, balances (docs/VISION.md row 3)
export const InvoiceStatus = z.enum(['draft', 'issued', 'void']);
export const PaymentMethod = z.enum(['cash', 'check', 'card', 'ach', 'zelle', 'qbo_payments', 'other']);
/** a payment row's method: how the money came in, or 'applied' (money already on account put on an invoice, decision 103) */
// 'credit_memo': a QuickBooks credit memo, money on account (decision 114)
export const PaymentRowMethod = z.enum([...PaymentMethod.options, 'applied', 'credit_memo']);
export const CreditReason = z.enum(['quality', 'short', 'price', 'returned', 'goodwill', 'other']);
export const InvoiceLine = z.object({
    id: Uuid, lineNo: z.number().int(), itemId: Uuid.nullable(), description: z.string(), qty: z.number(), unit: z.string().nullable(),
    /** negative on a discount or credit line (QuickBooks writes them on its invoices) */
    unitPrice: z.number().finite(), amount: z.number().finite(),
    /** what the line said before it was shorted (decision 95); null when it never was: qty is what was delivered */
    qtyOrdered: z.number().nullable().default(null),
    /** a credit line made in the app (decision 188): the product at a negative price, and why */
    creditReason: CreditReason.nullable().optional(),
});
/**
 * A credit on an invoice, JL's way (decision 188): the product at a negative price, as he does in QuickBooks. Not a sale:
 * no case off the shelf, no pick, no floor or cost check; counted as a credit by its reason. unitPrice is the credit for
 * one (a positive number; the line prints it below zero).
 */
export const InvoiceCreditLine = z.object({
    itemId: Uuid, qty: Qty.refine((n) => Math.round(n * 1000) > 0, 'At least 0.001'), unit: UnitCode.default('CS'),
    unitPrice: Money.refine((n) => Math.round(n * 100) > 0, 'A credit is at least a cent'), reason: CreditReason,
});
// ---- lost sales and quotes with their reason (decision 97)
export const LostSaleReason = z.enum(['out_of_stock', 'price', 'quality', 'late', 'competitor', 'not_carried', 'other']);
export const LostSaleChannel = z.enum(['phone', 'visit', 'text', 'whatsapp', 'portal', 'walkin', 'email', 'other']);
export const LostSaleCreate = z
    .object({
    occurredOn: IsoDate.optional(),
    customerId: Uuid.optional(),
    itemId: Uuid.optional(),
    /** an item we do not carry, as they said it */
    itemText: z.string().trim().min(1).max(200).optional(),
    qty: z.number().positive().max(100000).optional(),
    unit: UnitCode.optional(),
    reason: LostSaleReason,
    /** the price we offered (a quote turned down) and the price they wanted or were offered elsewhere */
    quotedPrice: z.number().nonnegative().max(100000).optional(),
    theirPrice: z.number().nonnegative().max(100000).optional(),
    competitor: z.string().trim().max(120).optional(),
    channel: LostSaleChannel.default('phone'),
    note: z.string().trim().max(500).optional(),
})
    .refine((v) => v.itemId !== undefined || v.itemText !== undefined, { message: 'An item from the list or its name', path: ['itemId'] });
export const LostSale = z.object({
    id: Uuid, occurredOn: IsoDate, customerId: Uuid.nullable(), customerName: z.string().nullable(), itemId: Uuid.nullable(), itemName: z.string(),
    qty: z.number().nullable(), unit: z.string().nullable(), reason: LostSaleReason, quotedPrice: z.number().nullable(), theirPrice: z.number().nullable(),
    competitor: z.string().nullable(), channel: LostSaleChannel, note: z.string().nullable(), recordedBy: z.string().nullable(), createdAt: z.string(),
    /** what it would have brought: the quantity at the price quoted, else the customer's price, else the standard tier (null without a quantity) */
    value: z.number().nullable(),
});
export const LostSalesReport = z.object({
    from: IsoDate, to: IsoDate,
    /** unvalued: entries with no quantity or no price, not in the value */
    total: z.object({ count: z.number().int(), value: z.number(), unvalued: z.number().int() }),
    /** more entries than the report reads at once: the sums cover the newest 5000 */
    truncated: z.boolean(),
    byReason: z.array(z.object({ reason: LostSaleReason, count: z.number().int(), value: z.number() })),
    /** the items lost most, each with its reasons: what to carry more of, or price better */
    byItem: z.array(z.object({ itemId: Uuid.nullable(), itemName: z.string(), count: z.number().int(), qty: z.number(), value: z.number(), outOfStock: z.number().int(), notCarried: z.number().int(), price: z.number().int() })),
    byCustomer: z.array(z.object({ customerId: Uuid, customerName: z.string(), count: z.number().int(), value: z.number() })),
    rows: z.array(LostSale),
});
/** Shorts on an issued invoice (decision 95): the quantity each line really delivered, lower than it says now. */
export const InvoiceShorts = z.object({
    lines: z.array(z.object({ lineId: Uuid, qty: z.number().min(0).max(100000).multipleOf(0.001) })).min(1).max(200),
    note: z.string().trim().max(500).optional(),
    /** the invoice already has credits for shorts: the person says these shorts are not the same cases */
    confirmCredits: z.boolean().optional(),
});
export const InvoiceAdjustmentKind = z.enum(['short', 'edit', 'add', 'remove']);
export const InvoiceAdjustment = z.object({
    id: Uuid, lineId: Uuid, lineNo: z.number().int(), description: z.string(),
    /** a short (delivered less), an edit (price or quantity corrected), a line added or taken off (decision 170) */
    kind: InvoiceAdjustmentKind.default('short'),
    qtyBefore: z.number(), qtyAfter: z.number(),
    /** null on shorts, which never change the price */
    /** below zero on a credit line (decision 188) */
    priceBefore: z.number().finite().nullable().default(null), priceAfter: z.number().finite().nullable().default(null),
    amountBefore: z.number().finite(), amountAfter: z.number().finite(), note: z.string().nullable(), recordedBy: z.string().nullable(), createdAt: z.string(),
});
/**
 * Edit an issued invoice made in the app (decision 170): the lines it should have now. A line with lineId is that line
 * (its price and quantity may change; a new product or unit is a new line); a line without one is added; a line of the
 * invoice not listed comes off. The total the person saw guards against a change made meanwhile.
 */
export const InvoiceEdit = z.object({
    expectedTotal: Money,
    /** when the invoice was last changed, as the screen read it: a change since that left the total alone is caught too */
    expectedUpdatedAt: z.string().datetime({ offset: true }).optional(),
    notes: z.string().max(1000).optional(),
    lines: z.array(OrderLineCreate.extend({ lineId: Uuid.optional() })).min(1).max(200),
    approveFloor: z.boolean().optional(),
    overrideHold: z.boolean().optional(),
    /** the invoice has credits for shorts: the person says the cases taken off now are other cases (decision 170) */
    confirmCredits: z.boolean().optional(),
    /** the credit lines it should have now (decision 188), each saved one by its lineId; left out, they stay as they are */
    credits: z.array(InvoiceCreditLine.extend({ lineId: Uuid.optional() })).max(50).optional(),
});
export const Invoice = z.object({
    id: Uuid, invoiceNo: z.number().int(), customerId: Uuid, customerName: z.string(), orderId: Uuid.nullable(), orderNo: z.number().int().nullable(),
    /** written by the QuickBooks mirror (decision 114): changed in QuickBooks; its order is linked by hand (decision 116) */
    fromQuickBooks: z.boolean().optional(),
    /** an invoice the app made and writes to QuickBooks (decision 185): where it stands there; null for the others */
    quickbooks: z.object({ state: z.enum(['waiting', 'sent', 'failed']), docNumber: z.string().nullable(), error: z.string().nullable(), at: z.string().nullable() }).nullable().optional(),
    /** typed in the app while QuickBooks holds the books (decision 140): practice, counted nowhere, deleted on the switch day */
    test: z.boolean().optional(),
    /** the customer's terms, printed on the invoice as PACA asks (7 CFR 46.46(e)) */
    termsDays: z.number().int().default(0),
    invoiceDate: IsoDate, dueDate: IsoDate.nullable(), status: InvoiceStatus,
    subtotal: Money, tax: Money, total: Money,
    /** the tax rate it was issued with (null on invoices from before it was kept, decision 95) */
    taxRate: z.number().nullable().default(null),
    /** null while the ledger has not started (settings.ledger.balance_from) or for invoices before it */
    paid: z.number().nullable(), balance: z.number().nullable(),
    notes: z.string().nullable(), lines: z.array(InvoiceLine), issuedAt: z.string().nullable(), voidedAt: z.string().nullable(), voidReason: z.string().nullable(),
    /** when the invoice row last changed (an edit guards on it, decision 170) */
    updatedAt: z.string().optional(),
    /** how it got there, from the route stop: who signed, when, and whether a drawn signature is on file */
    /** its order has not gone out yet (not delivered, not a walk-in or transfer): a void asks whether it still does */
    orderToGo: z.boolean().optional(),
    delivery: z.object({ stopId: Uuid, deliveredAt: z.string().nullable(), receivedBy: z.string().nullable(), hasSignature: z.boolean() }).nullable().default(null),
    /** the address it is billed and shipped to, from the customer's delivery card (one invoice only; the list leaves it null) */
    billTo: z.object({ name: z.string(), address: z.array(z.string()) }).nullable().default(null),
    shipTo: z.object({ name: z.string(), address: z.array(z.string()) }).nullable().default(null),
});
export const InvoiceSummary = Invoice.omit({ lines: true, billTo: true, shipTo: true });
/** The most invoices one list returns (the screen says so when a range reaches it). */
export const INVOICE_LIST_MAX = 5000;
/** What the invoice screen knows per item for one customer: the rate to fill in and what QuickBooks cannot tell you beside it. */
export const PriceSheetRow = z.object({
    itemId: Uuid, itemName: z.string(), packUnit: UnitCode, packDesc: z.string().nullable(), category: z.string().nullable(),
    price: Money.nullable(), tierPrice: Money.nullable(), source: PriceSource.nullable(),
    /** owner and office only */
    cost: Money.nullable(), floorPrice: Money.nullable(),
    lastPrice: Money.nullable(), lastQty: z.number().nullable(), lastDate: IsoDate.nullable(),
    onHand: z.number(),
    /** what one box holds converts (decision 166): the unit (LB, EA, BN), one unit's share of a box, the engine's price for
     *  one, and whether the product is offered that way (marked sold loose: the Unit dropdown) */
    loose: z.object({ unit: LooseUnit, perPack: z.number().positive(), price: Money.nullable(), offered: z.boolean().default(true) }).nullable().default(null),
    /** what one box holds is not known yet (decision 191): picking lb or each asks for it once, and it is saved on the product */
    boxUnknown: z.boolean().optional(),
});
/** The invoice screen (decision 60): one customer, lines typed in, created and issued in one step. */
export const InvoiceCreate = z.object({
    customerId: Uuid,
    /** an order already taken (the order day, decision 175): its lines become these, and it is the one invoiced */
    orderId: Uuid.optional(),
    /** that order as the screen saw it (OrderDayPreview.version): a change made meanwhile is refused */
    orderVersion: z.string().max(64).optional(),
    invoiceDate: IsoDate.optional(),
    channel: z.enum(['phone', 'sms', 'whatsapp', 'walkin']).default('phone'),
    notes: z.string().max(1000).optional(),
    /** with orderId, lineNo names the order's line it is (kept in place, its notes and delivery kept); without, a new line */
    lines: z.array(OrderLineCreate.extend({ lineNo: z.number().int().positive().optional() })).min(1).max(200),
    approveFloor: z.boolean().optional(),
    overrideHold: z.boolean().optional(),
    siteId: Uuid.optional(),
    /** credits on this invoice (decision 188), after its lines; the invoice total never goes below zero */
    credits: z.array(InvoiceCreditLine).max(50).optional(),
});
export const InvoiceFromOrder = z.object({ invoiceDate: IsoDate.optional(), notes: z.string().max(1000).optional() });
export const InvoiceVoid = z.object({
    reason: z.string().trim().min(1).max(500),
    /** the invoice has credits: they stay on the customer's account as money to apply; the void needs this said out loud */
    keepCredits: z.boolean().optional(),
    /** the invoice has credit lines (decision 188): they go with the void; the person says they will give them again */
    dropCreditLines: z.boolean().optional(),
    /**
     * its order, when it has not been delivered: 'cancel' takes it off the pick list and the route (nothing goes out);
     * 'keep' (the default) puts it back to be invoiced again. A delivered order always goes back to delivered, and so does
     * one whose stop took money, a signature or a photo at the door (kept, to be sorted out on Dispatch).
     */
    order: z.enum(['keep', 'cancel']).optional(),
});
/**
 * Delete an invoice (decision 193): an issued one is voided first (the same rules: no money on it, credit lines said to
 * go, its order kept or cancelled), then it is gone from every list, report and statement, deleted in QuickBooks too;
 * who, when and why stay on Activity. A void one is deleted as it is.
 */
export const InvoiceDelete = InvoiceVoid.omit({ keepCredits: true });
export const PaymentCreate = z.object({
    customerId: Uuid,
    invoiceId: Uuid.optional(),
    amount: z.number().positive().multipleOf(0.01),
    method: PaymentMethod,
    reference: z.string().trim().max(120).optional(),
    note: z.string().trim().max(500).optional(),
    receivedAt: z.string().datetime({ offset: true }).optional(),
});
export const Payment = z.object({
    id: Uuid, customerId: Uuid, invoiceId: Uuid.nullable(), invoiceNo: z.number().int().nullable(), amount: z.number(), method: PaymentRowMethod,
    receivedAt: z.string(), reference: z.string().nullable(), note: z.string().nullable(), reversesPaymentId: Uuid.nullable(),
    /** the rows of one check spread over invoices, or of one application of money on account, share it (decision 103) */
    groupId: Uuid.nullable().default(null),
    /** practice money (decision 187), shown only in the statement's Practice view */
    test: z.boolean().optional(),
});
/** Money put on invoices: each invoice once, at most 200 */
export const PaymentApplications = z.array(z.object({ invoiceId: Uuid, amount: z.number().positive().multipleOf(0.01) })).max(200)
    .refine((a) => new Set(a.map((x) => x.invoiceId)).size === a.length, 'Each invoice once');
/** One payment received across several invoices; what the applications leave goes on account (decision 103) */
export const PaymentReceive = z.object({
    customerId: Uuid,
    amount: z.number().positive().multipleOf(0.01),
    method: PaymentMethod,
    reference: z.string().trim().max(120).optional(),
    note: z.string().trim().max(500).optional(),
    receivedAt: z.string().datetime({ offset: true }).optional(),
    applications: PaymentApplications,
});
/** Money on account put on invoices: the applications given, or the oldest invoices first when none are */
export const ApplyOnAccount = z.object({
    applications: PaymentApplications.optional(),
    note: z.string().trim().max(500).optional(),
});
export const PaymentReceipt = z.object({
    groupId: Uuid, customerId: Uuid,
    /** received (a payment) or applied (money on account) */
    amount: z.number(),
    /** money on account the customer has after this */
    onAccount: z.number(),
    payments: z.array(Payment),
});
// ---- end of day (decision 105): the day's money counted, taken to the bank, the day signed off
/** cash or a check received and not yet in a deposit */
export const UndepositedPayment = z.object({
    paymentId: Uuid, customerId: Uuid, customerName: z.string(), method: z.enum(['cash', 'check']), amount: Money, reference: z.string().nullable(),
    receivedAt: z.string(), invoiceNo: z.number().int().nullable(), routeName: z.string().nullable(),
    /** the rows of one check spread over invoices share it: one check on the slip (decision 103) */
    groupId: Uuid.nullable(),
});
export const DepositCreate = z.object({
    paymentIds: z.array(Uuid).min(1).max(5000).refine((a) => new Set(a).size === a.length, 'Each payment once'),
    depositedOn: IsoDate,
    account: z.string().trim().max(60).optional(),
    note: z.string().trim().max(500).optional(),
});
export const Deposit = z.object({
    id: Uuid, depositNo: z.number().int(), depositedOn: IsoDate, account: z.string().nullable(), cash: Money, checks: Money, total: Money, note: z.string().nullable(),
    createdBy: z.string().nullable(), createdAt: z.string(),
    items: z.array(UndepositedPayment),
});
export const DepositSummary = Deposit.omit({ items: true }).extend({ count: z.number().int() });
export const DayRouteCash = z.object({ routeId: Uuid, name: z.string(), driverName: z.string().nullable(), cash: Money, checks: Money, turnedInAmount: Money.nullable(), variance: z.number().nullable() });
export const DaySummary = z.object({
    date: IsoDate,
    invoices: z.object({ count: z.number().int(), total: Money }),
    credits: Money,
    /** money received that day by how it came in (applications of money on account are not money received) */
    received: z.array(z.object({ method: z.string(), count: z.number().int(), amount: z.number() })),
    receivedTotal: z.number(),
    routes: z.array(DayRouteCash),
    stopsNotDelivered: z.number().int(),
    deliveredNotInvoiced: z.number().int(),
    /** all cash and checks not yet taken to the bank, whatever day they came in */
    undeposited: z.object({ cash: Money, checks: Money, count: z.number().int() }),
    closed: z.object({ at: z.string(), by: z.string().nullable(), cashCounted: Money.nullable(), note: z.string().nullable() }).nullable(),
    /** a closed day shows the numbers kept that evening; true when the live numbers have moved since (a void, a reversal) */
    changedSinceClose: z.boolean(),
});
export const DayClose = z.object({ date: IsoDate, cashCounted: Money.multipleOf(0.01).optional(), note: z.string().trim().max(500).optional() });
export const CreditCreate = z.object({
    customerId: Uuid, invoiceId: Uuid.optional(), amount: z.number().positive().multipleOf(0.01), reason: CreditReason, reasonText: z.string().trim().max(500).optional(),
});
export const Credit = z.object({
    id: Uuid, customerId: Uuid, invoiceId: Uuid.nullable(), invoiceNo: z.number().int().nullable(), amount: z.number(), reason: CreditReason, reasonText: z.string().nullable(), createdAt: z.string(),
    /** a reversal row points at the credit it undoes; a credit undone says so (decision 184) */
    reversesCreditId: Uuid.nullable().default(null), reversed: z.boolean().default(false),
    /** practice (decision 187) */
    test: z.boolean().optional(),
});
export const Statement = z.object({
    customerId: Uuid, customerName: z.string(), termsDays: z.number().int(),
    /** null until the ledger starts */
    balanceFrom: IsoDate.nullable(),
    invoiced: z.number(), paid: z.number(), credited: z.number(), balance: z.number().nullable(),
    /** what they owed on the ledger start (decision 74); part of balance */
    opening: z.number().default(0),
    /** what is still owed of it: money not on an invoice pays it first, so a payment fills this before any invoice */
    openingOwed: z.number().default(0),
    /** money on account that can still be put on invoices (decision 103) */
    toApply: z.number().default(0),
    aging: AgingBuckets.nullable().default(null), creditLimit: Money.nullable().default(null), onHold: z.boolean().default(false),
    openInvoices: z.array(z.object({ id: Uuid, invoiceNo: z.number().int(), date: IsoDate, dueDate: IsoDate.nullable(), total: z.number().finite(), balance: z.number(), daysLate: z.number().int(), test: z.boolean().optional() })),
    payments: z.array(Payment), credits: z.array(Credit),
    /** QuickBooks holds the books (decision 126): real money is entered there; practice is typed in the Practice view */
    quickbooksHolds: z.boolean().optional(),
    /** this is the Practice view (decision 187): practice invoices and money shown beside the real ones */
    practice: z.boolean().optional(),
});
export const LedgerSettings = z.object({ balanceFrom: IsoDate.nullable() });
// ---- month-end (decision 106)
/** the books closed through this day: nothing dated on or before it changes */
export const BooksSettings = z.object({ closedThrough: IsoDate.nullable() });
export const InventoryValueRow = z.object({
    itemId: Uuid, sku: z.string().nullable(), name: z.string(), category: z.string().nullable(), packUnit: z.string(),
    onHand: z.number(), value: z.number(),
    /** packs on hand with no cost known (no lot cost, no cost on file by that day): not in the value */
    unvalued: z.number(),
});
export const InventoryValue = z.object({ asOf: IsoDate, siteId: Uuid.nullable(), total: z.number(), rows: z.array(InventoryValueRow) });
// ---- email (decision 93): invoices and statements to customers, with a private link to open them
export const EmailStatus = z.object({
    /** the server has the provider's keys (POSTMARK_SERVER_TOKEN, EMAIL_FROM) */
    configured: z.boolean(), from: z.string().nullable(),
    /** sending to customers is on; off, only a test to a staff address goes */
    live: z.boolean(), fromName: z.string(), replyTo: z.string().nullable(),
});
export const EmailSettings = z.object({ live: z.boolean(), fromName: z.string().trim().min(1).max(80).regex(/^[^\x00-\x1f\x7f"<>\\]+$/, 'No quotes, angle brackets, backslashes or control characters'), replyTo: z.string().trim().toLowerCase().email().max(200).nullable() });
export const DocumentEmailSend = z.object({
    to: z.array(z.string().trim().toLowerCase().email().max(200)).min(1).max(10),
    message: z.string().trim().max(2000).optional(),
    /** a test: the subject says so, it goes only to staff (a user's address or the sender's domain), even while email to customers is off */
    test: z.boolean().default(false),
});
export const EmailLogEntry = z.object({
    id: Uuid, kind: z.enum(['invoice', 'statement']), invoiceId: Uuid.nullable(), to: z.array(z.string()), subject: z.string(), test: z.boolean(),
    /** unknown: the provider did not answer, so it may have gone; check before sending again */
    status: z.enum(['sending', 'sent', 'failed', 'unknown']), error: z.string().nullable(), sentBy: z.string().nullable(), createdAt: z.string(), sentAt: z.string().nullable(),
    /** when the customer first opened the link, and how many times */
    openedAt: z.string().nullable(), openCount: z.number().int(),
    /** the link no longer opens (revoked by the office, or the invoice was voided) */
    linkId: Uuid.nullable().default(null), revokedAt: z.string().nullable().default(null),
});
// ---- Square payment links (decision 94)
export const SquarePaymentRow = z.object({
    id: Uuid, squarePaymentId: z.string(), amount: z.number(), fee: z.number().nullable(), sourceType: z.string().nullable(), paidAt: z.string(),
    test: z.boolean(), invoiceId: Uuid.nullable(), invoiceNo: z.number().int().nullable(), customerName: z.string().nullable(),
    /** the app's payment it became (null for a test) */
    paymentId: Uuid.nullable(),
});
export const SquareStatus = z.object({
    /** the server has the keys (SQUARE_ACCESS_TOKEN, SQUARE_LOCATION_ID, SQUARE_WEBHOOK_SIGNATURE_KEY) */
    configured: z.boolean(), sandbox: z.boolean(),
    /** customers see Pay on their emailed invoice */
    live: z.boolean(),
    /** the address to give Square for the webhook */
    webhookUrl: z.string(),
    recent: z.array(SquarePaymentRow),
});
export const SquareSettings = z.object({ live: z.boolean() });
export const PayLink = z.object({ url: z.string().url() });
/** What the customer sees behind the emailed link: the invoice as printed, nothing internal. */
/** The invoice email before it is sent (decision 171): what it says and whether its Pay button will be there. */
export const EmailPreviewAsk = z.object({ message: z.string().trim().max(2000).optional(), test: z.boolean().optional() });
export const EmailPreview = z.object({
    subject: z.string(), html: z.string(),
    /** Square takes the balance (decision 94): the email's button is "View and pay" */
    payOnline: z.boolean(),
    /** why there is no Pay button: a TEST invoice, Square off (until the switch), nothing owed, a payment clearing */
    payOff: z.enum(['test', 'square', 'paid', 'pending']).nullable(),
    /** a TEST invoice (decision 140) goes only as a test, to staff */
    testInvoice: z.boolean(),
});
export const PublicInvoice = z.object({
    kind: z.literal('invoice'),
    invoice: Invoice.omit({ orderId: true, delivery: true, voidReason: true, issuedAt: true, customerId: true, voidedAt: true, updatedAt: true }).extend({
        lines: z.array(InvoiceLine.omit({ itemId: true })),
    }),
    paca: PacaSettings,
    /** the letterhead */
    company: CompanySettings.nullable().default(null),
    /** the customer's language: the page opens in it */
    language: z.enum(['en', 'es']),
    /** a Pay button: Square is set up and on, and the invoice has a balance (decision 94) */
    payOnline: z.boolean().default(false),
    /** a payment made online is clearing (a bank payment takes days): no Pay button meanwhile */
    payPending: z.boolean().default(false),
});
export const PublicStatement = z.object({
    kind: z.literal('statement'),
    customerName: z.string(), asOf: IsoDate, termsDays: z.number().int(), balance: z.number().nullable(), aging: AgingBuckets.nullable(),
    openInvoices: Statement.shape.openInvoices,
    /** what was owed on the ledger start, still owed; and payments and credits on account (below zero): with the open invoices they add up to the balance */
    opening: z.number(), onAccount: z.number(),
    language: z.enum(['en', 'es']),
});
/** What a customer owed on the ledger start, entered once from the CPA's books (decision 74); below zero = paid ahead. */
export const OpeningBalanceRow = z.object({
    customerId: Uuid, customerName: z.string(), active: z.boolean(),
    /** the opening balance at the current ledger start: the sum of its entries */
    amount: z.number().finite(),
    dueDate: IsoDate.nullable(), note: z.string().nullable(), entries: z.number().int(),
    /** the customer's balance today, the opening balance included */
    balance: z.number().finite(),
});
export const OpeningBalances = z.object({ balanceFrom: IsoDate.nullable(), total: z.number().finite(), rows: z.array(OpeningBalanceRow) });
/** Set customers' opening balances: each line is the total wanted; the difference is written as a new entry. */
export const OpeningBalancesPut = z.object({
    lines: z.array(z.object({
        customerId: Uuid,
        amount: z.number().finite().multipleOf(0.01).min(-10_000_000).max(10_000_000),
        dueDate: IsoDate.nullable().optional(),
        note: z.string().trim().max(200).optional(),
    })).min(1).max(5000),
});
export const VendorSummary = z.object({ id: Uuid, name: z.string(), deliveryDays: z.array(z.number().int()) });
// ---- warehouse: receiving with lots, shrink with a cause, the pick list (docs/VISION.md row 4)
export const Grade = z.enum(['good', 'fair', 'reject']);
export const ShrinkReason = z.enum(['age', 'damaged', 'temperature', 'over_ordered', 'short_dated', 'sample', 'other']);
export const ReceiptLineCreate = z.object({
    itemId: Uuid,
    qty: z.number().positive(),
    unit: UnitCode.optional(),
    unitCost: z.number().nonnegative().optional(),
    grade: Grade.optional(),
    origin: z.string().trim().max(80).optional(),
    vendorLot: z.string().trim().max(60).optional(),
    bestBy: IsoDate.optional(),
    note: z.string().trim().max(200).optional(),
});
export const ReceiptCreate = z.object({
    vendorId: Uuid,
    purchaseOrderId: Uuid.optional(),
    locationId: Uuid.optional(),
    siteId: Uuid.optional(), // when no locationId: the site whose cooler receives it (default: the hub)
    receivedAt: z.string().datetime({ offset: true }).optional(),
    documentRef: z.string().trim().max(60).optional(),
    note: z.string().trim().max(500).optional(),
    freight: z.number().nonnegative().default(0),
    fees: z.number().nonnegative().default(0), // fuel, pallets, broker
    allocate: z.enum(['packs', 'weight']).default('packs'), // how freight and fees spread over the lines
    lines: z.array(ReceiptLineCreate).min(1).max(200),
});
export const Lot = z.object({
    id: Uuid, lotCode: z.string(), itemId: Uuid, itemName: z.string(), vendorId: Uuid.nullable(), vendorName: z.string().nullable(),
    locationId: Uuid, receivedAt: z.string(), qtyReceived: z.number(), unit: z.string().nullable(),
    /** per pack; null for roles that do not see costs */
    unitCost: Money.nullable(),
    /** per pack with freight and fees allocated; null when unknown or hidden */
    landedCost: Money.nullable().default(null),
    grade: Grade.nullable(), origin: z.string().nullable(), vendorLot: z.string().nullable(), bestBy: IsoDate.nullable(),
    onHandPacks: z.number(), ageDays: z.number().int(),
});
export const Receipt = z.object({
    id: Uuid, vendorId: Uuid, vendorName: z.string(), locationId: Uuid, locationName: z.string(), receivedAt: z.string(),
    documentRef: z.string().nullable(), note: z.string().nullable(), receivedBy: z.string().nullable(),
    purchaseOrderId: Uuid.nullable(), purchaseOrderNo: z.string().nullable(),
    freight: Money.default(0), fees: Money.default(0), allocate: z.enum(['packs', 'weight']).default('packs'),
    lines: z.array(Lot), totalPacks: z.number(), totalCost: Money.nullable(), totalLanded: Money.nullable().default(null),
});
export const ReceiptSummary = Receipt.omit({ lines: true }).extend({ lineCount: z.number().int() });
export const ShrinkCreate = z.object({
    itemId: Uuid, lotId: Uuid.optional(), locationId: Uuid.optional(), siteId: Uuid.optional(), qty: z.number().positive(), reason: ShrinkReason, note: z.string().trim().max(300).optional(),
});
export const ShrinkResult = z.object({ movementId: Uuid, itemId: Uuid, lotId: Uuid.nullable(), qty: z.number(), onHandPacks: z.number() });
export const PickLot = z.object({ lotId: Uuid, lotCode: z.string(), onHandPacks: z.number(), ageDays: z.number().int() });
export const PickLine = z.object({ orderLineId: Uuid, itemId: Uuid, itemName: z.string(), qty: Qty, unit: UnitCode, note: z.string().nullable(), lots: z.array(PickLot) });
export const PickOrder = z.object({ orderId: Uuid, orderNo: z.number().int(), customerName: z.string(), status: OrderStatus, callAhead: z.boolean(), lines: z.array(PickLine) });
export const PickTotal = z.object({ itemId: Uuid, itemName: z.string(), qty: z.number(), onHandPacks: z.number(), short: z.number() });
export const PickList = z.object({ date: IsoDate, locationId: Uuid, locationName: z.string(), orders: z.array(PickOrder), totals: z.array(PickTotal) });
/**
 * The prep card (decision 179): an invoice dated in the range with its product lines as the order fillers build them.
 * A line sold by the pound or the piece out of the box carries its tag (LB, EACH, DZ...) so nobody pulls a whole case for it;
 * a note is only what someone typed on the line. Fees, discounts and credits are not lines here.
 */
export const PrepLine = z.object({ name: z.string(), qty: z.number(), tag: z.enum(['LB', 'OZ', 'EACH', 'DZ', 'BUNCH']).nullable(), note: z.string().nullable() });
export const PrepOrder = z.object({
    invoiceId: Uuid, invoiceNo: z.number().int(), customerName: z.string(), invoiceDate: IsoDate,
    fromQuickBooks: z.boolean(), test: z.boolean(), lines: z.array(PrepLine),
    /** what the card says, as a fingerprint: sent back when it is printed */
    hash: z.string(),
    /** when its card was last printed; changed: the card or the ticket would read differently now (reprint it) */
    cardPrintedAt: z.string().nullable(), changed: z.boolean(),
    /** when its 4 × 6 Zebra ticket was last printed (decision 180) */
    ticketsPrintedAt: z.string().nullable(),
    /** each print on its own: the card, or the ticket, would read differently now (changed: either) */
    cardChanged: z.boolean(), ticketsChanged: z.boolean(),
});
export const PrepList = z.object({ from: IsoDate, to: IsoDate, orders: z.array(PrepOrder) });
export const PrepPrinted = z.object({ kind: z.enum(['card', 'tickets']), prints: z.array(z.object({ invoiceId: Uuid, hash: z.string().regex(/^[A-Za-z0-9_-]{22}$/) })).min(1).max(300) });
// ---- reports: who is making money (docs/VISION.md row 4b)
export const ProfitRow = z.object({
    id: Uuid, name: z.string(), revenue: z.number(), cost: z.number().nullable(), margin: z.number().nullable(),
    /** margin as a share of the revenue whose cost is known, in percent */
    marginPct: z.number().nullable(),
    /** share of revenue (0..1) with a cost on file; below 1 the margin is partial */
    costCoverage: z.number(),
    lines: z.number().int(), invoices: z.number().int(),
    /** by customer: stops delivered in the period, and the share of what was ordered that was delivered (pack lines) */
    stops: z.number().int().nullable().default(null),
    fillRate: z.number().nullable().default(null),
    /** by item: what was thrown out in the period, at cost */
    shrinkCost: z.number().nullable().default(null),
    /** the margin after delivery (by customer: stops x cost per stop) or after shrink (by item); null when not known */
    netMargin: z.number().nullable().default(null),
    /** by item: what the register (Clover) sold in the period and its margin where the cost is known (decision 156) */
    retailRevenue: z.number().nullable().default(null),
    retailMargin: z.number().nullable().default(null),
});
// ---- the register's categories (decision 159): only the categories the owner keeps come in from Clover
export const CloverCategories = z.object({
    configured: z.boolean(),
    /** null: nothing chosen yet, every register line comes in */
    filter: z.object({ categories: z.array(z.string()), uncategorized: z.boolean() }).nullable(),
    lastPullAt: z.string().nullable(),
    categories: z.array(z.object({ id: z.string(), name: z.string(), items: z.number().int(), sold90: z.number(), kept: z.boolean(), /** made in Clover since the owner chose: kept until the owner decides */ isNew: z.boolean().default(false) })),
    /** items in no category (and amounts typed with no item) */
    uncategorized: z.object({ items: z.number().int(), sold90: z.number(), kept: z.boolean() }),
    /** register sales of the last 90 days the app holds now */
    sold90: z.number(),
});
export const CloverFilterPut = z.object({
    /** true: keep every category again */
    keepAll: z.boolean().default(false),
    categories: z.array(z.string().min(1).max(64)).max(500).default([]),
    uncategorized: z.boolean().default(false),
});
export const CloverFilterSaved = z.object({ removed: z.number().int(), readAgain: z.boolean(), status: CloverCategories });
// ---- the linking sheet (decision 157): every unlinked vendor code, QuickBooks product and sales name in one Excel file
// box_content (decision 158): what one box of a product holds, so a sale by the pound or each has a cost
export const LinkKind = z.enum(['vendor_code', 'qbo_item', 'name', 'box_content']);
/** One filled row read back: what it is, the product picked, and what applying it would do (or why it cannot). */
export const LinkingRow = z.object({
    kind: LinkKind, ref: z.string(), rowNo: z.number().int(), label: z.string(),
    typed: z.string(), itemId: Uuid.nullable(), productName: z.string().nullable(),
    /** guess: the product the app guessed, left as it was; linked only when the person says the guesses were checked;
     *  set: a box content to write on the product (box_content rows) */
    action: z.enum(['link', 'guess', 'set', 'ignore', 'done', 'problem']),
    problem: z.string().nullable(),
    /** box_content rows: what one box holds */
    content: z.number().nullable().default(null),
    contentUnit: ContentUnit.nullable().default(null),
    /** qbo_item rows: sold as pounds/each of the product's box; null = by the box (decision 166) */
    unit: LooseUnit.nullable().default(null),
});
export const LinkingPreview = z.object({
    rows: z.array(LinkingRow),
    counts: z.object({ link: z.number().int(), guess: z.number().int(), set: z.number().int().default(0), ignore: z.number().int(), done: z.number().int(), problem: z.number().int() }),
});
export const LinkingApply = z.object({
    rows: z.array(z.object({
        kind: LinkKind, ref: z.string().min(1).max(600), itemId: Uuid.optional(), ignore: z.literal(true).optional(),
        content: z.number().min(0.001).max(999_999).optional(), contentUnit: ContentUnit.optional(),
        /** qbo_item rows: the unit its quantities are in (decision 166) */
        unit: LooseUnit.nullable().optional(),
    })).min(1).max(50),
});
/** The QuickBooks products and sales names the owner said are not products, to ask about again (decision 158). */
export const LinkingIgnored = z.object({
    rows: z.array(z.object({ kind: z.enum(['qbo_item', 'name']), ref: z.string(), label: z.string(), where: z.string().nullable() })),
});
export const LinkingUnignore = z.object({ kind: z.enum(['qbo_item', 'name']), ref: z.string().min(1).max(600) });
export const LinkingApplied = z.object({
    results: z.array(z.object({ ref: z.string(), kind: LinkKind, ok: z.boolean(), detail: z.string() })),
});
/** Why some sales of a period have no margin, biggest first, so the fix is plain (decision 156). */
export const ProfitGaps = z.object({
    /** invoice lines sold with no product (QuickBooks products not linked, names not named) */
    noProduct: z.object({ revenue: z.number(), lines: z.number().int() }),
    /** products sold with no cost: no price those days (no_price), sold by the pound/each with no box content (no_content), or in a unit the content cannot convert (no_convert) */
    noCost: z.array(z.object({ itemId: Uuid, itemName: z.string(), revenue: z.number(), lines: z.number().int(), reason: z.enum(['no_price', 'no_content', 'no_convert']) })),
    /** the register's sales with no product linked */
    retailNoProduct: z.object({ revenue: z.number(), lines: z.number().int() }),
});
/** The register's (Clover) sales of the period at cost (decision 156): beside the invoices, never added into them. */
export const ProfitRetail = z.object({
    revenue: z.number(), cost: z.number(), margin: z.number(), marginPct: z.number().nullable(),
    /** share of the register's revenue (0..1) with a product and a known cost */
    costCoverage: z.number(),
});
export const ProfitReport = z.object({
    from: IsoDate, to: IsoDate, by: z.enum(['customer', 'item']), rows: z.array(ProfitRow),
    totals: z.object({ revenue: z.number(), cost: z.number(), margin: z.number(), marginPct: z.number().nullable(), costCoverage: z.number(), netMargin: z.number().nullable().default(null) }),
    /** what one delivery stop costs (settings.margin, decision 111); 0 when not set */
    costPerStop: z.number().default(0),
    gaps: ProfitGaps.nullable().default(null),
    retail: ProfitRetail.nullable().default(null),
});
export const MarginSettings = z.object({ costPerStop: z.number().nonnegative().max(10_000).multipleOf(0.01) });
/** A published price set when the cost was different (decision 111): the cost moved since, the price did not. */
export const StalePrice = z.object({
    itemId: Uuid, itemName: z.string(), sku: z.string().nullable(), packUnit: z.string(),
    tierCode: z.string(), price: Money, priceSince: IsoDate,
    costThen: Money.nullable(), costNow: Money, changePct: z.number().nullable(),
    marginThenPct: z.number().nullable(), marginNowPct: z.number(),
    floorPrice: Money.nullable(), underFloor: z.boolean(),
    /** what the item sold in the last 30 days, to rank what matters */
    sold30: z.number(),
});
/** What a customer's peers buy that they do not (decision 111): ideas for the next call. */
export const Suggestion = z.object({
    itemId: Uuid, itemName: z.string(), packUnit: z.string(), packDesc: z.string().nullable(),
    /** how many of the customer's peers (same price tier, buying in the last 90 days) bought it, of how many */
    peers: z.number().int(), of: z.number().int(),
    price: Money.nullable(),
    /** the last time this customer bought it, if ever */
    lastBought: IsoDate.nullable(),
});
// ---- the weekly exceptions: what went against the rules, with the money it cost (docs/VISION.md row 13)
export const ExceptionLine = z.object({
    invoiceId: Uuid, invoiceNo: z.number().int(), date: IsoDate, customerId: Uuid, customerName: z.string(), itemId: Uuid, itemName: z.string(),
    qty: z.number(), unitPrice: Money, cost: Money.nullable(), floorPrice: Money.nullable(), tierPrice: Money.nullable(),
    belowCost: z.boolean(), underFloor: z.boolean(), underTier: z.boolean(),
    gap: z.number(), // money left on the table against the reference (floor, else tier), for the qty
});
export const ExceptionsReport = z.object({
    from: IsoDate, to: IsoDate,
    pricing: z.array(ExceptionLine), // under floor or under tier, biggest gap first
    credits: z.array(z.object({ reason: z.string(), count: z.number().int(), amount: Money })),
    shrink: z.array(z.object({ reason: z.string(), packs: z.number(), cost: z.number().nullable(), /** some movements have no cost: the cost is at least this */ partial: z.boolean().default(false) })),
    voided: z.object({ count: z.number().int(), amount: Money }),
    cancelledOrders: z.number().int(),
    unpricedLines: z.number().int(),
    totals: z.object({ pricingGap: z.number(), belowCostLines: z.number().int(), creditsAmount: Money, shrinkCost: z.number().nullable(), shrinkCostPartial: z.boolean().default(false), shrinkPacks: z.number() }),
});
// ---- standing orders: the pattern a customer orders on, drafted for each delivery day (docs/VISION.md)
export const StandingLine = z.object({ itemId: Uuid, qty: Qty, unit: UnitCode.default('CS'), note: z.string().max(200).optional() });
export const StandingOrderPut = z.object({
    weekdays: z.array(z.number().int().min(1).max(7)).min(1),
    startsOn: IsoDate.optional(),
    endsOn: IsoDate.nullable().optional(),
    notes: z.string().max(1000).nullable().optional(),
    active: z.boolean().default(true),
    lines: z.array(StandingLine).min(1).max(100),
});
export const StandingOrder = z.object({
    id: Uuid, customerId: Uuid, customerName: z.string(), weekdays: z.array(z.number().int()), startsOn: IsoDate, endsOn: IsoDate.nullable(),
    notes: z.string().nullable(), active: z.boolean(),
    lines: z.array(StandingLine.extend({ id: Uuid, lineNo: z.number().int(), itemName: z.string() })),
});
export const StandingRun = z.object({ date: IsoDate, created: z.number().int(), skipped: z.number().int(), /** customers on hold or over their limit: no order made */ held: z.number().int().optional(), heldCustomers: z.array(z.string()).optional(), orders: z.array(z.object({ orderId: Uuid, orderNo: z.number().int(), customerName: z.string(), unpriced: z.number().int() })) });
// ---- price intelligence: what is this item doing (docs/VISION.md row 6)
export const CompetitorPriceSource = z.enum(['customer_invoice', 'quote', 'price_list', 'shelf', 'word_of_mouth', 'other']);
export const Competitor = z.object({ id: Uuid, name: z.string() });
export const CompetitorPriceCreate = z.object({
    competitorId: Uuid.optional(),
    competitorName: z.string().trim().min(1).max(80).optional(),
    price: z.number().nonnegative(),
    unit: UnitCode.optional(),
    seenOn: IsoDate.optional(),
    source: CompetitorPriceSource,
    customerId: Uuid.optional(),
    note: z.string().trim().max(300).optional(),
});
export const CompetitorPrice = z.object({
    id: Uuid, competitorId: Uuid, competitorName: z.string(), price: Money, unit: z.string().nullable(), seenOn: IsoDate, source: CompetitorPriceSource,
    customerName: z.string().nullable(), note: z.string().nullable(), recordedBy: z.string().nullable(),
});
export const WeekPoint = z.object({ week: IsoDate, value: z.number().nullable(), n: z.number().int() });
// ---- price history (decision 123): what we paid, what we set, what we charged, for any range
export const PriceHistoryQuery = z.object({ from: IsoDate.optional(), to: IsoDate.optional() });
/** money in a history may be negative (a return line) */
const Amount = z.number().finite();
export const ItemPriceHistory = z.object({
    itemId: Uuid, itemName: z.string(), packUnit: z.string().nullable(), from: IsoDate, to: IsoDate,
    /**
     * every price paid or quoted, newest first: the dock (landed, with the vendor's price and freight), bills, lists, typed
     * costs, placed orders (source order; order_cancelled when that order was cancelled, order_replaced when it was
     * reopened and placed again)
     */
    paid: z.array(z.object({ date: IsoDate, vendorName: z.string(), source: z.string(), cost: Amount.nullable(), vendorCost: Amount.nullable(), freight: Amount.nullable(), unit: z.string().nullable() })),
    /** every price we set, newest first: published list prices per tier, and customers' own prices (ended ones too) */
    set: z.array(z.object({
        date: IsoDate, kind: z.enum(['list', 'rule']), tierCode: z.string().nullable(), customerName: z.string().nullable(),
        price: Amount.nullable(), marginPct: z.number().nullable(), costBasis: Amount.nullable(), until: IsoDate.nullable(), endedOn: IsoDate.nullable(), note: z.string().nullable(),
        /** who set it (decision 161): the person who published or made the rule, "auto" when the price followed cost by itself */
        by: z.string().nullable().default(null),
        /** a list price: auto (follows cost) or hand (the owner's) */
        mode: z.enum(['auto', 'hand']).nullable().default(null),
    })),
    /**
     * what we charged, week by week (issued invoices before credits, in packs). avgPrice is over the lines that convert
     * to packs; margin and marginPct are over the lines whose cost is known, costKnown says what share of the revenue that is
     */
    weeks: z.array(z.object({ week: IsoDate, packs: z.number(), revenue: Amount, avgPrice: Amount.nullable(), avgCost: Amount.nullable(), margin: Amount.nullable(), marginPct: z.number().nullable(), costKnown: z.number() })),
    /** what each customer paid in the range; lastPrice is in lastUnit (a line by the pound is per pound) */
    customers: z.array(z.object({ customerId: Uuid, customerName: z.string(), packs: z.number(), revenue: Amount, avgPrice: Amount.nullable(), lastPrice: Amount.nullable(), lastUnit: z.string().nullable(), lastOn: IsoDate, marginPct: z.number().nullable(), costKnown: z.number() })),
});
export const ItemIntel = z.object({
    itemId: Uuid, itemName: z.string(), packUnit: UnitCode,
    /** current cost per vendor (owner/office; empty otherwise) */
    costs: z.array(z.object({ vendorId: Uuid, vendorName: z.string(), cost: Money, effectiveDate: IsoDate, source: z.string(), prior: Money.nullable() })),
    /** weekly average cost from observations, last 12 weeks (owner/office) */
    costWeeks: z.array(WeekPoint),
    /** weekly average sold price and cases, last 12 weeks (issued invoices) */
    soldWeeks: z.array(WeekPoint.extend({ packs: z.number() })),
    /** price per tier now */
    tiers: z.array(z.object({ code: z.string(), name: z.string(), price: Money.nullable(), source: z.string().nullable() })),
    /** what each customer paid last, newest first (at most 30) */
    customers: z.array(z.object({ customerId: Uuid, customerName: z.string(), price: Money, qty: z.number(), on: IsoDate, rule: z.string().nullable() })),
    competitors: z.array(CompetitorPrice),
    /** margin %, last 4 weeks vs the 4 before (owner/office) */
    marginNow: z.number().nullable(), marginBefore: z.number().nullable(),
    onHandPacks: z.number(), soldLast7: z.number(), soldLast28: z.number(),
});
// ---- vendor documents: invoices and price lists, typed in or photographed, feeding the cost ledger
export const VendorDocType = z.enum(['price_list', 'invoice', 'credit_memo', 'statement', 'other']);
export const VendorDocLineCreate = z.object({
    vendorCode: z.string().trim().max(60).optional(),
    description: z.string().trim().max(200).optional(),
    itemId: Uuid.optional(),
    qty: z.number().nonnegative().optional(),
    unit: UnitCode.optional(),
    unitPrice: z.number().nonnegative(),
});
export const VendorDocCreate = z.object({
    vendorId: Uuid,
    docType: VendorDocType,
    docNumber: z.string().trim().max(60).optional(),
    docDate: IsoDate,
    dueDate: IsoDate.optional(), // an invoice: default docDate + the vendor's terms
    total: z.number().optional(),
    notes: z.string().trim().max(1000).optional(),
    lines: z.array(VendorDocLineCreate).min(1).max(300),
});
export const VendorDocLine = z.object({
    id: Uuid, lineNo: z.number().int(), vendorCode: z.string().nullable(), description: z.string().nullable(), itemId: Uuid.nullable(), itemName: z.string().nullable(),
    qty: z.number().nullable(), unit: z.string().nullable(), unitPrice: Money.nullable(), lineTotal: Money.nullable(), flags: z.array(z.string()),
});
/** What the AI reader proposes from a photo or PDF: the same form, pre-filled; a person checks and saves. */
export const VendorDocDraft = z.object({
    vendorId: Uuid.nullable(), vendorName: z.string().nullable(), vendorNameOnDocument: z.string().nullable(),
    docType: VendorDocType, docNumber: z.string().nullable(), docDate: IsoDate.nullable(), total: Money.nullable(),
    lines: z.array(z.object({
        vendorCode: z.string().nullable(), description: z.string().nullable(), qty: z.number().nullable(), unit: z.string().nullable(), unitPrice: Money.nullable(), amount: Money.nullable(),
        itemId: Uuid.nullable(), itemName: z.string().nullable(), itemPackUnit: z.string().nullable(),
    })),
    notes: z.array(z.string()),
    duplicateOf: z.object({ id: Uuid, docNumber: z.string().nullable(), docDate: IsoDate }).nullable(), // the same file was read before
});
export const VendorDoc = z.object({
    id: Uuid, vendorId: Uuid, vendorName: z.string(), docType: VendorDocType, docNumber: z.string().nullable(), docDate: IsoDate, total: Money.nullable(),
    dueDate: IsoDate.nullable().default(null), paid: Money.default(0),
    status: z.string(), notes: z.string().nullable(), hasFile: z.boolean(), fileName: z.string().nullable(), readBy: z.string(), createdAt: z.string(),
    lines: z.array(VendorDocLine), costsRecorded: z.number().int(), unmapped: z.number().int(),
});
export const VendorDocSummary = VendorDoc.omit({ lines: true }).extend({ lineCount: z.number().int() });
// ---- vendor papers read in the background into history (P5, decision 146)
export const PaperStatus = z.enum(['waiting', 'reading', 'posted', 'held', 'skipped', 'failed']);
export const Paper = z.object({
    id: Uuid, source: z.enum(['upload', 'gmail', 'takeout']), filename: z.string(), mimeType: z.string(), bytes: z.number().int(),
    receivedAt: z.string(), status: PaperStatus, reason: z.string().nullable(), attempts: z.number().int(), senderVerified: z.boolean().default(true),
    vendorId: Uuid.nullable(), vendorName: z.string().nullable(), docType: z.string().nullable(), docNumber: z.string().nullable(),
    docDate: IsoDate.nullable(), total: z.number().nullable(), lineCount: z.number().int().nullable(), documentId: Uuid.nullable(), readAt: z.string().nullable(),
    /** held over the reader's note or a price jump: a person may keep it (decision 149) */
    keepable: z.boolean().default(false),
    /** the vendor's name as the reader read it off the paper (a held paper whose vendor was not recognised is named by it) */
    vendorNameRead: z.string().nullable().default(null),
});
export const PaperList = z.object({
    counts: z.object({ waiting: z.number().int(), reading: z.number().int(), posted: z.number().int(), held: z.number().int(), skipped: z.number().int(), failed: z.number().int() }),
    paused: z.boolean(), lastError: z.string().nullable(), lastErrorAt: z.string().nullable(),
    readerConfigured: z.boolean(), storageConfigured: z.boolean(),
    items: z.array(Paper),
});
export const PaperAdded = z.object({ id: Uuid, duplicate: z.boolean() });
// a person keeps held papers (decision 149): judged again from what was read, the soft holds waived
export const PaperKeep = z.object({ ids: z.array(Uuid).min(1).max(300) });
// held papers judged again from what was read, free (decision 151); none: every held one, newest first, 300 at a time
export const PaperRecheck = z.object({ ids: z.array(Uuid).min(1).max(300).optional() });
export const PaperVendorName = z.object({ name: z.string().trim().min(1).max(200), vendorId: Uuid });
export const PaperKeepResult = z.object({ results: z.array(z.object({ id: Uuid, outcome: z.enum(['posted', 'held', 'skipped', 'failed', 'waiting', 'stop', 'not-held']), reason: z.string().nullable() })) });
// old mail brought in from a Google Takeout file (P5 part 3, decision 148): which of these mail attachments are in already
export const MailPapersKnown = z.object({ refs: z.array(z.string().min(1).max(700)).min(1).max(200) });
export const MailPapersKnownResult = z.object({ known: z.array(z.string()) });
// the owner's mailbox read for vendor papers (P5 part 2, decision 147)
export const GmailStatus = z.object({
    configured: z.boolean(),
    redirectUri: z.string().nullable(),
    connection: z.object({ email: z.string(), label: z.string(), trustedDomains: z.array(z.string()), connectedAt: z.string(), lastReadAt: z.string().nullable(), lastResult: z.string().nullable() }).nullable(),
});
export const GmailRead = z.object({ ok: z.boolean(), detail: z.string().nullable() });
export const FileInfo = z.object({ id: Uuid, filename: z.string(), mimeType: z.string(), bytes: z.number().int() });
// ---- purchasing: purchase orders
export const PurchaseOrderLineCreate = z.object({ itemId: Uuid, qty: z.number().positive(), unit: UnitCode.optional(), unitCost: z.number().nonnegative().optional() });
export const PurchaseOrderCreate = z.object({ vendorId: Uuid, notes: z.string().max(1000).optional(), lines: z.array(PurchaseOrderLineCreate).min(1).max(300) });
export const PurchaseOrder = z.object({
    id: Uuid, poNo: z.string().nullable(), vendorId: Uuid, vendorName: z.string(), status: z.enum(['draft', 'sent', 'confirmed', 'received', 'closed', 'cancelled']),
    notes: z.string().nullable(), createdAt: z.string(),
    lines: z.array(z.object({ id: Uuid, lineNo: z.number().int(), itemId: Uuid.nullable(), itemName: z.string().nullable(), qtyOrdered: z.number(), qtyReceived: z.number().nullable(), unit: z.string().nullable(), unitCost: Money.nullable() })),
    totalPacks: z.number(), totalCost: Money.nullable(),
});
// ---- the owner's dashboard: what is going on, without looking for it (docs/VISION.md row 13)
export const Dashboard = z.object({
    date: IsoDate,
    orders: z.object({ today: z.number().int(), byStatus: z.record(z.string(), z.number().int()), tomorrow: z.number().int(), unpricedLines: z.number().int() }),
    money: z.object({
        ledgerStarted: z.boolean(),
        invoicedToday: z.number(), invoicedWeek: z.number(), paidWeek: Money,
        openBalance: z.number().nullable(), overdueBalance: z.number().nullable(), overdueCustomers: z.number().int(),
        marginWeekPct: z.number().nullable(),
    }),
    slowPayers: z.array(z.object({ customerId: Uuid, customerName: z.string(), balance: z.number(), oldestDaysLate: z.number().int() })),
    stockAtRisk: z.array(z.object({ lotId: Uuid, lotCode: z.string(), itemId: Uuid, itemName: z.string(), onHandPacks: z.number(), ageDays: z.number().int(), shelfLife: ShelfLifeClass })),
    standing: z.object({ due: z.number().int(), drafted: z.number().int() }),
    inbox: z.object({ unknownNames: z.number().int(), unmappedDocLines: z.number().int(), openPurchaseOrders: z.number().int(), texts: z.number().int().default(0) }),
});
// ---- signals (decision 55): how are things, with direction. Every number compared with a prior period.
export const SignalDelta = z.object({ now: z.number(), before: z.number(), pct: z.number().nullable() });
export const SignalsMover = z.object({ id: Uuid, name: z.string(), now: z.number(), before: z.number(), pct: z.number().nullable() });
export const Signals = z.object({
    date: IsoDate,
    /** the last 7 days ending today, against the 7 before and the same 7 days a year ago; cost-bearing parts are null for roles that may not see costs */
    week: z.object({ from: IsoDate, to: IsoDate }),
    revenue: z.object({ week: SignalDelta, lastYear: SignalDelta, fourWeeksAvg: SignalDelta }),
    margin: z.object({ week: SignalDelta, pctNow: z.number().nullable(), pctBefore: z.number().nullable(), coveredPct: z.number() }).nullable(),
    orders: z.object({ week: SignalDelta, activeCustomers: SignalDelta, avgTicket: SignalDelta }),
    /** open: the customers' ledger balance (below zero when more was paid ahead than is owed); overdue: past due after unapplied money (decision 73) */
    receivables: z.object({ ledgerStarted: z.boolean(), open: z.number().finite().nullable(), overdue: Money.nullable(), dsoDays: z.number().nullable(), dsoBefore: z.number().nullable(), paid: SignalDelta }),
    customers: z.object({
        growing: z.array(SignalsMover), slipping: z.array(SignalsMover), quiet: z.array(z.object({ id: Uuid, name: z.string(), lastOrderDate: IsoDate, daysSince: z.number().int(), usualGapDays: z.number().int(), weeklyBefore: Money })),
        new: z.array(z.object({ id: Uuid, name: z.string(), firstInvoiceDate: IsoDate, revenue: z.number() })),
    }),
    items: z.object({ up: z.array(SignalsMover), down: z.array(SignalsMover) }),
    costs: z.array(z.object({ itemId: Uuid, itemName: z.string(), vendorName: z.string().nullable(), now: Money, before: Money, pct: z.number(), effectiveDate: IsoDate })),
    shrink: z.object({ weekPacks: z.number(), beforePacks: z.number(), weekCost: Money.nullable(), byCause: z.array(z.object({ cause: z.string(), packs: z.number() })) }),
    competitorPricesSeen: z.number().int(),
    errors24h: z.number().int(),
    /** the learning loop (decision 58): how close the plain forecast came over the last 28 days, 100 = perfect, weighted by what sold; null until the scorer has run */
    forecast: z.object({
        days: z.number().int(),
        scoredDays: z.number().int(),
        accuracy: z.object({ weekdayAvg: z.number().nullable(), lastWeek: z.number().nullable() }),
        worst: z.array(z.object({ itemId: Uuid, itemName: z.string(), forecast: z.number(), actual: z.number(), offBy: z.number() })),
    }),
});
// ---- sales analysis (decision 84): the period against the one before and the same days a year ago, a trend per
// bucket, the mix by category and the rows by customer and item. Cost-bearing parts are null for roles without costs.
export const SalesGrain = z.enum(['day', 'week', 'month']);
export const SalesKpi = z.object({ now: z.number(), prior: z.number(), lastYear: z.number(), pct: z.number().nullable(), lyPct: z.number().nullable() });
export const SalesRow = z.object({
    id: Uuid, name: z.string(),
    revenue: z.number(), prior: z.number(), change: z.number(), pct: z.number().nullable(), share: z.number(),
    grossProfit: z.number().nullable(), gmPct: z.number().nullable(),
    invoices: z.number().int(),
    /** qty sold in the item's own unit (cases mostly); 0 on customer rows, where units do not add up */
    qty: z.number(),
    /** revenue per bucket of the period, oldest first: the row's trend line */
    trend: z.array(z.number()),
});
export const SalesAnalysis = z.object({
    from: IsoDate, to: IsoDate, priorFrom: IsoDate, priorTo: IsoDate, lyFrom: IsoDate, lyTo: IsoDate, grain: SalesGrain,
    showCost: z.boolean(),
    kpis: z.object({
        revenue: SalesKpi, grossProfit: SalesKpi.nullable(),
        /** gross margin percent on the lines with a known cost: now, the prior period and a year ago (points, not a ratio) */
        gmPct: z.object({ now: z.number().nullable(), prior: z.number().nullable(), lastYear: z.number().nullable() }).nullable(),
        coveredPct: z.number(),
        invoices: SalesKpi, avgInvoice: SalesKpi, customers: SalesKpi,
    }),
    /** one bucket per day, week (Monday) or month of the period; days is how many of its days fall in the period */
    series: z.array(z.object({ start: IsoDate, days: z.number().int(), revenue: z.number(), lastYear: z.number(), grossProfit: z.number().nullable(), gmPct: z.number().nullable(), invoices: z.number().int() })),
    categories: z.array(z.object({ name: z.string(), sort: z.number().int(), revenue: z.number(), prior: z.number(), share: z.number() })),
    customers: z.array(SalesRow),
    items: z.array(SalesRow),
});
// ---- QuickBooks Online (decision 85): the connection the owner makes once, and a read-only check
export const QboEnvironment = z.enum(['production', 'sandbox']);
export const QboStatus = z.object({
    /** the app's keys from Intuit are set on the server */
    configured: z.boolean(),
    redirectUri: z.string().nullable(),
    connection: z
        .object({
        realmId: z.string(), environment: QboEnvironment, companyName: z.string().nullable(),
        connectedAt: z.string(), connectedByName: z.string().nullable(),
        /** after this the owner must connect again (Intuit's limit is 100 days without use) */
        refreshExpiresAt: z.string().nullable(), refreshedAt: z.string().nullable(),
    })
        .nullable(),
    /** the copy of QuickBooks kept in the app (decision 86): records per kind, whether a pull runs now, the last one */
    history: z
        .object({
        running: z.boolean(),
        /** an import that could not start (a scheduled job held the lock) */
        startError: z.string().nullable().default(null),
        counts: z.record(z.string(), z.number().int()),
        lastRun: z.object({ finishedAt: z.string(), ok: z.boolean(), rows: z.number().int(), detail: z.string().nullable() }).nullable(),
    })
        .default({ running: false, startError: null, counts: {}, lastRun: null }),
});
export const QboConnectStart = z.object({ environment: QboEnvironment });
export const QboPullStarted = z.object({ started: z.boolean() });
export const QboConnectUrl = z.object({ url: z.string().url() });
// ---- QuickBooks mirror, step 2a (decision 98): which QuickBooks name is which in the app
export const QboLinkEntity = z.enum(['Customer', 'Vendor', 'Item']);
export const QboUnlinked = z.object({ entity: QboLinkEntity, qboId: z.string(), name: z.string(), active: z.boolean().nullable(), balance: z.number().nullable() });
export const QboMatching = z.object({
    connected: z.boolean(),
    /** per kind: how many QuickBooks records, how many linked, and app records with no QuickBooks twin */
    counts: z.array(z.object({ entity: QboLinkEntity, qbo: z.number().int(), linked: z.number().int(), appOnly: z.number().int() })),
    /** QuickBooks records with no app record yet (active ones first, the biggest balance first) */
    unlinked: z.array(QboUnlinked),
    /** app records to pick from when linking by hand: customers and vendors with no QuickBooks twin, every product (decision 155) */
    appOnly: z.array(z.object({ entity: QboLinkEntity, id: Uuid, name: z.string(), archived: z.boolean() })),
    /** what is linked, how and by whom, to check it and undo a wrong link */
    linked: z.array(z.object({ entity: QboLinkEntity, qboId: z.string(), qboName: z.string(), appId: Uuid, appName: z.string(), how: z.enum(['key', 'name', 'alias', 'manual', 'created', 'pushed']), linkedBy: z.string().nullable(), linkedAt: z.string(), unit: LooseUnit.nullable().default(null) })),
});
export const QboLinkCreated = z.object({ appId: Uuid });
/**
 * Matching closed out (plan C10, decision 189): what the switch still needs linked. Blocking: QuickBooks customers with a
 * balance or activity in the last year, and products sold in the last year (not marked "not a product"). Said, not
 * blocking: vendors with bills in the last year, and the app's customers and products QuickBooks would get new.
 */
export const QboSwitchMatching = z.object({
    connected: z.boolean(),
    /** the first day of "the last year" */
    since: IsoDate.nullable(),
    /** nothing blocking is left: the fresh start may run */
    ready: z.boolean(),
    customers: z.array(z.object({ qboId: z.string(), name: z.string(), balance: z.number().nullable(), lastActivity: IsoDate.nullable(), transactions: z.number().int() })),
    products: z.array(z.object({ qboId: z.string(), name: z.string(), sold: z.number(), lines: z.number().int(), lastSold: IsoDate.nullable() })),
    vendors: z.array(z.object({ qboId: z.string(), name: z.string(), lastActivity: IsoDate.nullable(), transactions: z.number().int() })),
    /** made new in QuickBooks on the first invoice, or refused there: a twin that is inactive, linked to another record,
     *  not a product to sell on a line, or (a customer) the name of a vendor or an employee */
    appCustomers: z.array(z.object({ id: Uuid, name: z.string(), why: z.enum(['new', 'inactive', 'taken', 'other_kind', 'not_sellable']) })),
    appProducts: z.array(z.object({ id: Uuid, name: z.string(), why: z.enum(['new', 'inactive', 'taken', 'other_kind', 'not_sellable']) })),
    /** the whole counts (a list is capped at 1,000 rows) */
    counts: z.object({ customers: z.number().int(), products: z.number().int(), vendors: z.number().int(), appCustomers: z.number().int(), appProducts: z.number().int() }),
});
/** Copy the customers or the products from QuickBooks (decision 190): what was (or would be) done, row by row. */
/** Why a record was linked or put away, and what kept one in use (decision 190); the screen says them in EN/ES. */
export const QboSyncWhy = z.enum(['same_name', 'same_words', 'inactive_qbo', 'not_in_qbo', 'per_unit_lb', 'per_unit_ea', 'per_unit_dz', 'per_unit_bn']);
export const QboSyncHold = z.enum(['store', 'key_account', 'portal', 'tier', 'orders', 'standing', 'owes', 'on_hand', 'purchase', 'register', 'qbo_deleted', 'box_unknown', 'no_product']);
const QboSyncRow = z.object({
    appId: Uuid.nullable(), qboId: z.string().nullable(), name: z.string(), was: z.string().nullable(),
    why: QboSyncWhy.nullable(), hold: QboSyncHold.nullable(),
    /** the number behind the hold (cases on hand, open lines, sign-ins) */
    n: z.number().nullable(),
    /** a customer kept: the QuickBooks customer made new that is likely the same one */
    maybe: z.string().nullable(),
    /** what was copied from QuickBooks where the app had nothing (customers) */
    fields: z.array(z.enum(['email', 'phone', 'address'])),
});
const SyncCount = z.number().int();
export const QboSync = z.object({
    applied: z.boolean(),
    linked: z.array(QboSyncRow), renamed: z.array(QboSyncRow), made: z.array(QboSyncRow), restored: z.array(QboSyncRow),
    archived: z.array(QboSyncRow), kept: z.array(QboSyncRow), filled: z.array(QboSyncRow),
    counts: z.object({ linked: SyncCount, renamed: SyncCount, made: SyncCount, restored: SyncCount, archived: SyncCount, kept: SyncCount, filled: SyncCount, inQbo: SyncCount }),
});
export const QboSyncAsk = z.object({ entity: z.enum(['Customer', 'Item']), apply: z.boolean().default(false) });
/** A QuickBooks product that is not a product (a fee, freight, a deposit): never asked about again (decision 157). */
export const QboNotAProduct = z.object({ qboId: z.string().min(1).max(40) });
export const QboMatchRun = z.object({ customers: z.number().int(), vendors: z.number().int(), items: z.number().int() });
export const QboLinkPut = z.object({
    entity: QboLinkEntity, qboId: z.string().min(1).max(40), appId: Uuid,
    /** products only: sold as pounds, each, dozens or bunches of the product's box; null = by the box. Sent again on a link already made, it changes the unit */
    unit: LooseUnit.nullable().optional(),
});
export const QboLinkRef = z.object({ entity: QboLinkEntity, qboId: z.string().min(1).max(40) });
/** What each customer owes: QuickBooks' open balance beside the app's (decision 98). */
export const QboBalances = z.object({
    connected: z.boolean(),
    /** import: the last import's balances; report: QuickBooks' own A/R aging report, read now (plan C11) */
    source: z.enum(['import', 'report']).default('import'),
    /** the report's own grand total (report only), to check the rows add up to what QuickBooks prints */
    reportTotal: z.number().nullable().default(null),
    /** the app's receivables ledger start; before it the app holds no balances */
    ledgerFrom: IsoDate.nullable(),
    /** when QuickBooks' balances were copied (the app's side is live) */
    asOf: z.string().nullable(),
    /** qbo and app: the linked customers on both sides; qboUnlinked and appUnlinked: what is owed outside the links */
    totals: z.object({ qbo: z.number(), app: z.number().nullable(), qboUnlinked: z.number(), appUnlinked: z.number().nullable(), customers: z.number().int(), differ: z.number().int() }),
    rows: z.array(z.object({ customerId: Uuid, customerName: z.string(), qboId: z.string(), qboBalance: z.number(), appBalance: z.number().nullable(), diff: z.number().nullable() })),
    /** money QuickBooks says is owed by customers not linked to the app yet */
    unlinkedOwing: z.array(z.object({ qboId: z.string(), name: z.string(), balance: z.number() })),
});
export const QboCheck = z.object({
    companyName: z.string().nullable(), legalName: z.string().nullable(), country: z.string().nullable(), fiscalYearStartMonth: z.string().nullable(),
    counts: z.object({ customers: z.number().int(), vendors: z.number().int(), items: z.number().int(), invoices: z.number().int(), payments: z.number().int(), bills: z.number().int(), accounts: z.number().int() }),
    oldestInvoiceDate: z.string().nullable(),
});
// ---- the Claude connector: API tokens (docs/VISION.md row 7)
export const ApiToken = z.object({
    id: Uuid, userId: Uuid, userName: z.string(), role: RoleCode, name: z.string(), prefix: z.string(), createdAt: z.string(), lastUsedAt: z.string().nullable(), revokedAt: z.string().nullable(),
    kind: z.enum(['pat', 'oauth']), // pat: made by the owner on the connector screen; oauth: a Claude app the person allowed
    expiresAt: z.string().nullable(),
});
// OAuth (Claude.ai connectors): what the consent screen needs and what it sends back.
export const OAuthClientInfo = z.object({ clientId: z.string(), clientName: z.string(), redirectUris: z.array(z.string()) });
export const OAuthApprove = z.object({
    clientId: z.string().min(1).max(200),
    redirectUri: z.string().url(),
    state: z.string().max(2000).optional(),
    codeChallenge: z.string().min(43).max(128),
    codeChallengeMethod: z.literal('S256'),
    resource: z.string().url().optional(),
    scope: z.string().max(200).optional(),
});
export const OAuthApproved = z.object({ redirectTo: z.string() });
export const ApiTokenCreate = z.object({ userId: Uuid, name: z.string().trim().min(1).max(80) });
/** The plain token, shown once. */
export const ApiTokenIssued = ApiToken.extend({ token: z.string() });
// ---- sites and companies: every place stock sits and every company that owns one (decision 46)
export const SiteKind = z.enum(['hub', 'store', 'truck']);
export const SiteCode = z.string().trim().regex(/^[A-Za-z0-9]{2,8}$/, '2-8 letters or digits').transform((s) => s.toUpperCase());
export const Company = z.object({ id: Uuid, code: z.string(), name: z.string(), active: z.boolean(), siteCount: z.number().int() });
export const CompanyCreate = z.object({ code: SiteCode, name: z.string().trim().min(1).max(120) });
export const SiteLocation = z.object({ id: Uuid, name: z.string(), kind: z.string(), active: z.boolean() });
export const Site = z.object({
    id: Uuid, code: z.string(), name: z.string(), kind: SiteKind, companyId: Uuid, companyName: z.string(), address: z.string().nullable(), active: z.boolean(),
    /** the customer record this store buys on when it belongs to another company; null otherwise */
    customerId: Uuid.nullable(),
    locations: z.array(SiteLocation),
});
export const SiteCreate = z.object({
    code: SiteCode, name: z.string().trim().min(1).max(120), kind: SiteKind, companyId: Uuid.optional(), // default: the home company
    address: z.string().trim().max(300).optional(),
    /** make (or link) the customer record a store of another company buys on; price tier for its transfers */
    customerId: Uuid.optional(),
});
export const SitePatch = z.object({ name: z.string().trim().min(1).max(120).optional(), address: z.string().trim().max(300).nullable().optional(), active: z.boolean().optional(), customerId: Uuid.nullable().optional() });
export const TransferStatus = z.enum(['draft', 'sent', 'received', 'cancelled']);
export const TransferLineCreate = z.object({ itemId: Uuid, qty: Qty, lotId: Uuid.optional(), note: z.string().trim().max(200).optional() });
export const TransferCreate = z.object({
    fromSiteId: Uuid.optional(), // default: the hub
    toSiteId: Uuid,
    note: z.string().trim().max(500).optional(),
    lines: z.array(TransferLineCreate).min(1).max(200),
});
export const TransferLine = z.object({
    id: Uuid, lineNo: z.number().int(), itemId: Uuid, itemName: z.string(), qty: z.number(), qtyReceived: z.number().nullable(),
    lotId: Uuid.nullable(), lotCode: z.string().nullable(), toLotCode: z.string().nullable(), note: z.string().nullable(),
});
export const Transfer = z.object({
    id: Uuid, transferNo: z.number().int(), status: TransferStatus,
    fromSiteId: Uuid, fromSiteName: z.string(), toSiteId: Uuid, toSiteName: z.string(),
    note: z.string().nullable(), orderId: Uuid.nullable(), orderNo: z.number().int().nullable(),
    sentAt: z.string().nullable(), receivedAt: z.string().nullable(), createdAt: z.string(),
    totalPacks: z.number(), lines: z.array(TransferLine),
});
export const TransferReceive = z.object({ lines: z.array(z.object({ lineId: Uuid, qtyReceived: z.number().nonnegative() })).optional() });
// ---- the prices screen and the price list (decision 48): set every tier price in one pass, with the engine and the intel beside it
export const PriceWorksheetTier = z.object({
    code: PriceTierCode,
    /** the price in force today (published, or from cost once tier_source is cost) */
    price: Money.nullable(),
    source: z.enum(['cost', 'published']).nullable(),
    /** what the engine would charge from today's cost at the tier margin */
    suggested: Money.nullable(),
    published: Money.nullable(),
    /** the floor for this item at this tier's cost: cost / (1 - floor); the floor is the product's or the tier's margin, the lower (decision 161) */
    floorPrice: Money.nullable(),
    /** auto: follows cost by itself; hand: the owner's, kept; null: published before decision 161 (kept as hand) */
    mode: z.enum(['auto', 'hand']).nullable().default(null),
    /** an auto price whose engine number moved past the hold: waiting for the owner (decision 161) */
    held: z.boolean().default(false),
});
/** USDA Market News beside our cost (P4, decision 144): the latest report day's range for a product's commodity. */
export const UsdaLatest = z.object({
    market: z.string(), date: IsoDate,
    /** the "mostly" range when USDA gives one, else low to high, across the rows of that day (sizes, origins) */
    low: z.number().nullable(), high: z.number().nullable(),
    package: z.string().nullable(), rows: z.number().int(),
});
export const UsdaRow = z.object({
    market: z.string(), date: IsoDate, commodity: z.string(), variety: z.string().nullable(), package: z.string().nullable(), size: z.string().nullable(),
    origin: z.string().nullable(), grade: z.string().nullable(), organic: z.boolean(),
    low: z.number().nullable(), high: z.number().nullable(), mostlyLow: z.number().nullable(), mostlyHigh: z.number().nullable(),
});
export const UsdaLink = z.object({ commodity: z.string().trim().min(1).max(120), variety: z.string().trim().max(120).nullable().optional(), package: z.string().trim().max(160).nullable().optional() });
export const ItemUsda = z.object({
    configured: z.boolean(),
    link: z.object({ commodity: z.string(), variety: z.string().nullable(), package: z.string().nullable() }).nullable(),
    /** commodities USDA reports whose words are in the product's name, when it is not linked yet */
    suggestions: z.array(z.string()),
    latest: z.array(UsdaLatest),
    /** the latest day per market, variety, package, size and origin, the last 30 days */
    rows: z.array(UsdaRow),
    /** week by week, the middle of the range per market, the last 12 weeks */
    weeks: z.array(z.object({ week: IsoDate, market: z.string(), mid: z.number() })),
    /** our cost now, for owner and office */
    ourCost: z.number().nullable(),
});
export const UsdaCommodity = z.object({ commodity: z.string(), rows: z.number().int(), latest: IsoDate, varieties: z.array(z.string()), packages: z.array(z.string()) });
export const PriceWorksheetRow = z.object({
    itemId: Uuid, itemName: z.string(), nameEs: z.string().nullable(), packUnit: UnitCode, packDesc: z.string().nullable(), sensitivity: Sensitivity,
    cost: Money.nullable(), costDate: IsoDate.nullable(), costPrior: Money.nullable(),
    /** where today's cost came from (decision 160): the vendor and what gave it (list, order, receipt, invoice, manual, import) */
    costVendor: z.string().nullable().default(null), costSource: z.string().nullable().default(null),
    /** average price we sold it for in the last 28 days (issued invoices) and the packs sold */
    soldAvg28: Money.nullable(), soldPacks28: z.number(),
    /** the lowest competitor price seen in the last 60 days and how many were seen */
    competitorLow: Money.nullable(), competitorName: z.string().nullable(), competitorN: z.number().int(),
    /** USDA Market News for the product's linked commodity (decision 144) */
    usda: UsdaLatest.nullable().optional(),
    tiers: z.array(PriceWorksheetTier),
});
export const PriceWorksheet = z.object({ tiers: z.array(PriceTier), tierSource: z.enum(['published', 'cost']), roundTo: z.number(), lastPublished: IsoDate.nullable(), rows: z.array(PriceWorksheetRow) });
export const PricePublish = z.object({
    effectiveDate: IsoDate.optional(),
    note: z.string().trim().max(300).optional(),
    prices: z.array(z.object({ itemId: Uuid, tierCode: PriceTierCode, price: Money, /** auto follows cost from now on; hand is kept (the default) */ mode: z.enum(['auto', 'hand']).optional() })).min(1).max(5000),
});
export const PricePublished = z.object({ priceListId: Uuid, effectiveDate: IsoDate, count: z.number().int(), unchanged: z.number().int() });
/** Every tier price that changed in a period, for all products (decision 161): the sheet's PRICE HISTORY. */
export const PriceChange = z.object({
    date: IsoDate, at: z.string(), itemId: Uuid, itemName: z.string(), tierCode: z.string(), tierName: z.string(),
    from: Money.nullable(), to: Money, costBasis: Money.nullable(),
    /** who published it; "auto" when it followed cost by itself */
    by: z.string().nullable(),
    mode: z.enum(['auto', 'hand']).nullable(),
});
export const PriceChanges = z.object({ from: IsoDate, to: IsoDate, rows: z.array(PriceChange), truncated: z.boolean() });
/** Reports → Costs and prices (decision 163): a vendor's prices against USDA, our cost and our prices, with the KPIs. */
export const CostDashRow = z.object({
    itemId: Uuid, itemName: z.string(), packUnit: z.string(),
    /** sold in the period: packs (case lines), money, the margin where costed (below zero when credits outweigh sales) */
    packs: z.number(), sales: z.number().finite(), margin: z.number().finite().nullable(), marginPct: z.number().nullable(),
    /** today's cost and where it came from (decisions 122, 160) */
    cost: z.number().finite().nullable(), costVendor: z.string().nullable(), costSource: z.string().nullable(),
    /** the chosen vendor's own price on the last day and on the first day of the period */
    vendorNow: z.number().finite().nullable(), vendorNowDate: IsoDate.nullable(), vendorThen: z.number().finite().nullable(), vendorChangePct: z.number().nullable(),
    /** the cheapest other vendor's newest price in the last 14 days */
    bestOther: z.object({ vendorName: z.string(), price: z.number().finite(), date: IsoDate }).nullable(),
    /** USDA Market News: the latest range in 30 days (the terminal market first) and its middle */
    usda: z.object({ market: z.string(), date: IsoDate, low: z.number().nullable(), high: z.number().nullable(), mid: z.number() }).nullable(),
    /** the vendor's price against the USDA middle, in percent (+12 = 12% above) */
    vsUsdaPct: z.number().nullable(),
    prices: z.array(z.object({ tierCode: z.string(), price: Money.nullable(), mode: z.enum(['auto', 'hand']).nullable(), gmPct: z.number().nullable() })),
});
export const CostDash = z.object({
    from: IsoDate, to: IsoDate,
    vendor: z.object({ id: Uuid, name: z.string() }).nullable(),
    vendors: z.array(z.object({ id: Uuid, name: z.string() })),
    tiers: z.array(z.object({ code: z.string(), name: z.string() })),
    kpis: z.object({
        sales: z.number().finite(), salesPrior: z.number().finite(), marginPct: z.number().nullable(), marginPctPrior: z.number().nullable(),
        /** the share of sales whose cost is known, in percent */
        costedPct: z.number().nullable(),
        /** the vendor's prices now against the period's first day, weighted by what each product sold */
        vendorChangePct: z.number().nullable(), vendorItems: z.number().int(),
        /** the vendor against USDA: the middle of the products linked to USDA */
        vsUsdaMedianPct: z.number().nullable(), usdaItems: z.number().int(),
        underCost: z.number().int(), underFloor: z.number().int(), auto: z.number().int(), hand: z.number().int(), priceChanges: z.number().int(),
    }),
    weeks: z.array(z.object({ week: IsoDate, sales: z.number().finite(), margin: z.number().finite().nullable() })),
    rows: z.array(CostDashRow),
});
/** One product's weeks: the vendor's price, the USDA middle and our price for a tier (decision 163). */
export const CostDashItem = z.object({
    itemId: Uuid, itemName: z.string(), vendorName: z.string().nullable(), tierCode: z.string(),
    weeks: z.array(z.object({ week: IsoDate, vendor: z.number().nullable(), usda: z.number().nullable(), ours: z.number().nullable() })),
});
export const PriceListRow = z.object({ itemId: Uuid, itemName: z.string(), nameEs: z.string().nullable(), packUnit: UnitCode, packDesc: z.string().nullable(), price: Money, family: z.string().nullable(), /** the item's category (Fruit, Vegetable, Dry goods, ...): the printed list's sections */ category: z.string().nullable().default(null) });
export const PriceList = z.object({
    tierCode: PriceTierCode, tierName: z.string(), customerId: Uuid.nullable(), customerName: z.string().nullable(), effectiveDate: IsoDate, rows: z.array(PriceListRow),
});
// ---- payables: what we owe vendors, with due dates, and the payments against it (decision 49)
export const VendorPaymentMethod = z.enum(['cash', 'check', 'card', 'ach', 'zelle', 'other']);
export const VendorPaymentCreate = z.object({
    vendorId: Uuid,
    documentId: Uuid.optional(),
    amount: z.number().positive().multipleOf(0.01),
    method: VendorPaymentMethod,
    reference: z.string().trim().max(120).optional(),
    note: z.string().trim().max(500).optional(),
    paidAt: z.string().datetime({ offset: true }).optional(),
});
export const VendorPayment = z.object({
    id: Uuid, vendorId: Uuid, vendorName: z.string(), documentId: Uuid.nullable(), docNumber: z.string().nullable(), amount: z.number(), method: VendorPaymentMethod,
    paidAt: z.string(), reference: z.string().nullable(), note: z.string().nullable(), reversesPaymentId: Uuid.nullable(),
});
export const PayableRow = z.object({
    documentId: Uuid, vendorId: Uuid, vendorName: z.string(), docNumber: z.string().nullable(), docDate: IsoDate, dueDate: IsoDate.nullable(),
    total: Money, paid: Money, balance: Money,
    /** negative = not due yet; 0 = due today; positive = days late */
    daysLate: z.number().int().nullable(), hasFile: z.boolean(),
});
export const Payables = z.object({
    asOf: IsoDate,
    totals: z.object({ open: Money, overdue: Money, dueThisWeek: Money, vendors: z.number().int() }),
    rows: z.array(PayableRow),
    recent: z.array(VendorPayment),
});
// ---- passkeys (face or fingerprint sign-in) and the system screen (decision 50)
export const Passkey = z.object({ id: Uuid, deviceLabel: z.string().nullable(), createdAt: z.string(), lastUsedAt: z.string().nullable() });
export const PasskeyOptions = z.object({ challengeId: Uuid, options: z.record(z.string(), z.unknown()) });
export const PasskeyRegisterVerify = z.object({ challengeId: Uuid, response: z.record(z.string(), z.unknown()), deviceLabel: z.string().trim().max(80).optional() });
export const PasskeyLoginVerify = z.object({ challengeId: Uuid, response: z.record(z.string(), z.unknown()) });
export const ClientError = z.object({ message: z.string().trim().min(1).max(500), detail: z.string().max(4000).optional(), path: z.string().max(300).optional(), version: z.string().max(40).optional() });
export const AppError = z.object({
    id: z.number().int(), at: z.string(), side: z.enum(['api', 'web']), status: z.number().int().nullable(), method: z.string().nullable(), path: z.string().nullable(),
    message: z.string(), detail: z.string().nullable(), requestId: z.string().nullable(), userName: z.string().nullable(), version: z.string().nullable(),
});
export const SystemHealth = z.object({
    version: z.string().nullable(), startedAt: z.string(), node: z.string(),
    database: z.object({ ok: z.boolean(), sizeMb: z.number(), connections: z.number().int(), migrations: z.number().int(), lastMigration: z.string().nullable() }),
    errors: z.object({ last24h: z.number().int(), last7d: z.number().int(), recent: z.array(AppError) }),
    storage: z.object({ configured: z.boolean() }), reader: z.boolean(), texting: z.boolean(),
    users: z.object({ active: z.number().int(), withPassword: z.number().int(), withPasskey: z.number().int(), signedInLast7d: z.number().int() }),
    sessions: z.object({ open: z.number().int() }),
});
export const JobRun = z.object({ id: Uuid, job: z.string(), startedAt: z.string(), finishedAt: z.string(), ok: z.boolean(), rows: z.number().int(), detail: z.string().nullable() });
/** The jobs the app runs on its own clock (decision 58), with each one's last run. */
export const JobStatus = z.object({
    name: z.string(), summary: z.string(), last: JobRun.nullable(),
    /** decision 153: ok; idle (ran with nothing to do: a key or a connection missing); failed; late (not run when it should have); never */
    state: z.enum(['ok', 'idle', 'failed', 'late', 'never']).default('ok'),
    everyMinutes: z.number().int().nullable().default(null),
});
export const JobList = z.object({ runAtHour: z.number().int(), jobs: z.array(JobStatus) });
// ---- traceability (decision 51): a lot's story, forward to customers and back to the vendor
export const LotTraceSale = z.object({ invoiceId: Uuid, invoiceNo: z.number().int(), invoiceDate: IsoDate, customerId: Uuid, customerName: z.string(), qty: z.number(), status: z.string() });
export const LotTrace = z.object({
    lot: Lot.extend({ siteName: z.string().nullable(), locationName: z.string(), grower: z.string().nullable(), receiptId: Uuid.nullable(), documentRef: z.string().nullable(), transferNo: z.number().int().nullable(), fromLotCode: z.string().nullable() }),
    sales: z.array(LotTraceSale),
    shrink: z.array(z.object({ at: z.string(), qty: z.number(), reason: z.string().nullable(), note: z.string().nullable() })),
    transfers: z.array(z.object({ transferNo: z.number().int(), toSiteName: z.string(), qty: z.number(), at: z.string(), toLotCode: z.string().nullable() })),
    soldPacks: z.number(), shrinkPacks: z.number(), transferredPacks: z.number(),
});
export const TraceRow = z.object({
    lotCode: z.string(), itemName: z.string(), vendorName: z.string().nullable(), origin: z.string().nullable(), grower: z.string().nullable(), vendorLot: z.string().nullable(), receivedAt: IsoDate,
    invoiceNo: z.number().int(), invoiceDate: IsoDate, customerName: z.string(), qty: z.number(), unit: z.string().nullable(),
});
// ---- the order day and customer patterns (decision 52)
export const Weekday = z.number().int().min(1).max(7);
export const OrderDaySetting = z.object({ weekday: Weekday, name: z.string(), lands: z.string().nullable(), sells: z.array(Weekday).min(1) });
export const PatternItem = z.object({ itemId: Uuid, itemName: z.string(), unit: UnitCode, timesOrdered: z.number().int(), ofOrders: z.number().int(), typicalQty: z.number(), lastQty: z.number(), lastOn: IsoDate });
export const CustomerPattern = z.object({
    customerId: Uuid, weeks: z.number().int(), orders: z.number().int(), ordersPerWeek: z.number(),
    /** share of the active weeks with an order on that weekday, 1 = Monday */
    byWeekday: z.array(z.object({ weekday: Weekday, share: z.number(), orders: z.number().int() })),
    usualDays: z.array(Weekday),
    items: z.array(PatternItem),
    lastOrderOn: IsoDate.nullable(), nextExpectedOn: IsoDate.nullable(),
    status: z.enum(['new', 'ok', 'due', 'overdue', 'quiet']),
    typicalTotal: Money.nullable(),
});
export const ExpectedOrder = z.object({ customerId: Uuid, customerName: z.string(), date: IsoDate, share: z.number(), hasOrder: z.boolean(), typicalTotal: Money.nullable(), items: z.array(z.object({ itemId: Uuid, itemName: z.string(), qty: z.number() })) });
/** A day's delivery work: stops (customers delivered to), orders, cases (pack units) and pick lines. */
export const WorkCounts = z.object({ stops: z.number(), orders: z.number(), cases: z.number(), lines: z.number() });
/**
 * The delivery workload ahead, for scheduling drivers (decision 90). Per day: what is booked, what that weekday
 * usually is (the average of the last weeks), a busy day for that weekday (4 weeks in 5 are at or under it), the
 * projection (the larger of booked and usual), and the drivers that takes. Read-only: the app proposes, JL schedules.
 */
export const Workload = z.object({
    from: IsoDate, to: IsoDate, weeksBack: z.number().int(), stopsPerDriver: z.number(),
    days: z.array(z.object({
        date: IsoDate, weekday: Weekday, holiday: z.string().nullable(),
        booked: WorkCounts, usual: WorkCounts, busy: WorkCounts, projected: WorkCounts,
        drivers: z.number().int(), driversBusy: z.number().int(),
        /** regulars whose pattern says they order this day and whose order is not in yet */
        notInYet: z.array(z.object({ customerId: Uuid, customerName: z.string(), share: z.number() })),
    })),
    /** each weekday over the last weeks: the usual and the busy day */
    weekdays: z.array(z.object({ weekday: Weekday, usual: WorkCounts, busy: WorkCounts })),
    /** how far off the usual-day forecast was over the last 4 weeks, in stops (null without enough history) */
    accuracy: z.object({ days: z.number().int(), meanStopsOff: z.number(), withinTwoPct: z.number() }).nullable(),
});
export const WorkloadSettings = z.object({ stopsPerDriver: z.number().positive().max(100) });
export const OrderDayCell = z.object({
    qty: z.number(), source: z.enum(['order', 'standing', 'pattern', 'none']),
    /** the order's line has no price for this customer: publish one before invoicing (decision 162) */
    noPrice: z.boolean().optional(),
});
export const OrderDayRow = z.object({
    itemId: Uuid, itemName: z.string(), packUnit: UnitCode, vendorId: Uuid.nullable(), vendorName: z.string().nullable(),
    base: z.number(), safety: z.number(), keep: z.number(), accounts: z.array(OrderDayCell), accountsTotal: z.number(),
    /** the key stores' demand their cells do not show: another order of theirs in the window, the standing quantity of days with no order yet */
    keyMore: z.number().default(0),
    others: z.number(), onHand: z.number(),
    /** what the window's orders were already invoiced for: it left on hand and still counts in the columns, so ORDER adds it back */
    invoicedBack: z.number().default(0),
    order: z.number(),
    /** part of ORDER already on another vendor's order for this load (bought a bit from each, decision 92) */
    alsoAt: z.array(z.object({ vendorName: z.string(), qty: z.number() })).default([]),
    /** every vendor's order for this load that carries the item, the usual vendor's too; placed = sent to the vendor (decision 154) */
    onOrders: z.array(z.object({ vendorId: Uuid, vendorName: z.string(), qty: z.number(), placed: z.boolean() })).default([]),
    cost: Money.nullable(),
});
export const OrderDay = z.object({
    load: OrderDaySetting, loadDate: IsoDate, sellFrom: IsoDate, sellTo: IsoDate, weeksBack: z.number().int(),
    accounts: z.array(z.object({
        customerId: Uuid, customerName: z.string(), shortName: z.string().nullable(), hasOrder: z.boolean(), nextExpectedOn: IsoDate.nullable(),
        /** the account's order inside the sell window, when one exists: what the column is typed into */
        orderId: Uuid.nullable().default(null), orderNo: z.number().int().nullable().default(null), orderStatus: OrderStatus.nullable().default(null),
        /** the account's other orders in the window not invoiced yet (a second delivery day) */
        otherOpen: z.number().int().default(0),
        /** a call-ahead column (not a key account): the customer the office picked for this load */
        extra: z.boolean().default(false),
        /** a call-ahead column the office keeps on this load's plan (decision 154); the others show because of an order */
        kept: z.boolean().default(false),
        /** a call-ahead customer with an order in the window: the column shows by itself, kept or not */
        byOrder: z.boolean().default(false),
    })),
    rows: z.array(OrderDayRow), totalPacks: z.number(), totalCost: Money.nullable(),
});
/** what one pack holds converted to a loose unit: pounds from lb or oz, each and dozens from a count, bunches */
const LOOSE_PER = { LB: { lb: 1, oz: 16 }, EA: { ct: 1, ea: 1 }, DZ: { ct: 12, ea: 12 }, BN: { bu: 1 } };
/**
 * The unit a product is sold in out of its box (decision 166): pounds for one counted in lb or oz, each for a count,
 * bunches for bunches; null when it is not sold loose or the box's content is not known.
 */
export function looseUnitOf(item) {
    if (!item.sellLoose || !(item.content && item.content > 0) || !item.contentUnit)
        return null;
    return item.contentUnit === 'lb' || item.contentUnit === 'oz' ? 'LB' : item.contentUnit === 'ct' || item.contentUnit === 'ea' ? 'EA' : item.contentUnit === 'bu' ? 'BN' : null;
}
/**
 * How a QuickBooks product is sold, guessed from its name (decision 166): "Jalapeno - weight" or "by the lb" is LB,
 * "Avocado - count" or "each" is EA. A box size ("Onions 50 lb", "Avocados 48 count", "Lemons 115ct") is not: a number
 * right before the word means the box. Null when nothing says so (the box). A person checks the guess.
 */
export function guessSoldAs(...texts) {
    const t = texts.filter(Boolean).join(' ').toLowerCase();
    if (/\b(weight|wt)\b|\b(by|per)\s+(the\s+)?(lb|lbs|pound)\b|por\s+libra|(?<![\d.,]\s*)\b(lbs?|pounds?|libras?)\b/.test(t))
        return 'LB';
    if (/\b(by|per)\s+(the\s+)?(piece|each|count)\b|por\s+pieza|(?<![\d.,]\s*)\b(count|ct|each|ea|pieces?|piezas?)\b/.test(t))
        return 'EA';
    return null;
}
/** One loose unit as a share of the box: 1 lb of a 25 lb box is 0.04; null when the content does not convert. */
export function packsPerUnit(unit, content, contentUnit) {
    const per = contentUnit ? LOOSE_PER[unit]?.[contentUnit] : undefined;
    return per !== undefined && content && content > 0 ? per / content : null;
}
/**
 * The engine's price for one loose unit (docs/CATALOG.md section 3, decision 166): the box price times the unit's share
 * of the box times split_factor_loose, rounded up to loose_round_to. $22 a 50 lb sack at 1.00 and $0.05: $0.44, so $0.45 a lb.
 */
export function loosePrice(boxPrice, perPack, factor, roundTo) {
    const raw = boxPrice * perPack * factor;
    const step = roundTo > 0 ? roundTo : 0.01;
    return Math.round(Math.ceil(Math.round((raw / step) * 1e6) / 1e6) * step * 100) / 100;
}
/**
 * The order day's ORDER for an item (decision 82), the one formula the server and the screen share. The base forecasts
 * what customers other than the key accounts take in a window, so what those customers already booked (the Other
 * columns and More others) is not added on top of it: their demand is the larger of the two. The key accounts'
 * columns are their own orders (or the guess for them) and add up.
 */
export function orderDayNeed(r) {
    // on hand below zero is a stock record that is missing (a sale with no receipt before it), not cases owed to the
    // cooler: it counts as none, so a missing receipt is never bought again (decision 165); the plan flags it to count
    const need = Math.max(r.base, r.booked) + r.safety + r.keep + r.keyStores - Math.max(0, r.onHand);
    return need > 0 ? Math.ceil(Math.round(need * 1000) / 1000) : 0;
}
/** What a key account ordered, typed into its column: replaces the account's order for the window (qty 0 drops the line; no lines cancels it). */
export const OrderDayAccountPut = z.object({
    date: IsoDate.optional(), load: Weekday.optional(),
    /** the order the column showed when it was typed into (null when none): a different one now means someone else typed first */
    orderId: Uuid.nullable().optional(),
    lines: z.array(z.object({ itemId: Uuid, qty: z.number().finite().nonnegative() })).max(500),
    /**
     * merge (decision 65): only the items sent change (0 removes one), every other line of the account's order stays as it is
     * in the database, and whoever made the order since this screen loaded is merged into rather than refused
     */
    merge: z.boolean().optional(),
});
/** call-ahead columns a load's plan can have beside the key accounts (decision 154) */
export const ORDER_DAY_CALL_AHEADS = 8;
export const OrderDayInvoice = z.object({ date: IsoDate.optional(), load: Weekday.optional(), /** the call-ahead columns' customers, invoiced with the key accounts */ extra: z.array(Uuid).max(ORDER_DAY_CALL_AHEADS).optional() });
// ---- vendor claims (decision 107): what a vendor owes back
export const VendorClaimKind = z.enum(['reject', 'short', 'billed', 'price']);
export const VendorClaim = z.object({
    key: z.string(), vendorId: Uuid, vendorName: z.string(), kind: VendorClaimKind,
    itemId: Uuid.nullable(), itemName: z.string(), qty: z.number(), unit: z.string().nullable(),
    /** per pack: the lot's or the order's cost, the billed price, or for a price claim the difference; null: no cost known */
    unitAmount: z.number().nullable(), amount: z.number().nullable(),
    day: IsoDate, ref: z.string().nullable(),
    /** the vendor bill behind a billed or price claim */
    documentId: Uuid.nullable(),
    /** no bill matched to this truck yet: the bill decides what is owed (rejects and shorts the vendor did not charge are not) */
    provisional: z.boolean(),
});
/** Matching bills to trucks: the trucks with no bill (and the bills they could be), the bills with the trucks they cover */
export const BillMatching = z.object({
    trucks: z.array(z.object({
        receiptId: Uuid, vendorId: Uuid, vendorName: z.string(), receivedAt: z.string(), documentRef: z.string().nullable(), lines: z.number().int(),
        candidates: z.array(z.object({ documentId: Uuid, docNumber: z.string().nullable(), docDate: IsoDate, total: z.number().nullable() })),
    })),
    bills: z.array(z.object({
        documentId: Uuid, vendorId: Uuid, vendorName: z.string(), docNumber: z.string().nullable(), docDate: IsoDate, total: z.number().nullable(),
        /** linked: set by the office (it can be undone); by number: the bill number typed at the dock */
        trucks: z.array(z.object({ receiptId: Uuid, receivedAt: z.string(), how: z.enum(['linked', 'number']) })),
    })),
});
export const ReceiptBillLink = z.object({ documentId: Uuid.nullable() });
export const VendorClaimSettle = z.object({
    key: z.string().min(3).max(200),
    outcome: z.enum(['credited', 'dismissed']),
    creditDocumentId: Uuid.optional(),
    note: z.string().trim().max(500).optional(),
});
// ---- QuickBooks money mirror, step 2b (decision 114)
export const QboMirrorResult = z.object({
    invoices: z.object({ created: z.number().int(), updated: z.number().int(), voided: z.number().int(), unchanged: z.number().int(), adopted: z.number().int(), appWritten: z.number().int().optional() }),
    payments: z.object({ written: z.number().int(), reversed: z.number().int(), unchanged: z.number().int() }),
    skipped: z.object({ customers: z.number().int(), invoices: z.number().int(), payments: z.number().int(), openBalance: z.number() }),
    problems: z.array(z.object({ kind: z.string(), qboId: z.string(), detail: z.string() })),
    dryRun: z.boolean(),
});
/** What turning the mirror on clears (decision 126), and the ledger start it sets. */
export const QboPracticeMoney = z.object({
    invoices: z.number().int(), invoiceTotal: z.number(), sheetsInvoices: z.number().int(), payments: z.number().int(), paymentTotal: z.number(), credits: z.number().int(), creditTotal: z.number(),
    openingCustomers: z.number().int(), ledgerFrom: z.string().nullable(), proposedFrom: z.string().nullable(),
    /** Sheets invoices that stay void: QuickBooks does not hold them for a matched customer, or a short was typed on them */
    sheetsStayVoid: z.number().int().default(0),
});
export const QboMirrorStatus = z.object({
    connected: z.boolean(),
    /** the owner turned it on: QuickBooks' money is the app's, kept in step every night */
    on: z.boolean(),
    last: QboMirrorResult.extend({ at: z.string() }).nullable(),
    /** what a run would do now (nothing is written) */
    preview: QboMirrorResult.nullable(),
    /** when the owner turned it on (decision 126) */
    turnedOnAt: z.string().nullable().default(null),
    /** a run is writing now (started by Run now, the hourly import or the turn-on) */
    running: z.boolean().default(false),
    /** the last money run failed, and why (cleared when one goes through) */
    runError: z.string().nullable().default(null),
    /** while off: what turning on would clear and the ledger start it would set; why it cannot be turned on yet */
    practice: QboPracticeMoney.nullable().default(null),
    blocked: z.string().nullable().default(null),
});
/** The owner turns the mirror on (decision 126): typed, after a backup. */
export const QboMirrorTurnOn = z.object({ confirm: z.literal('TURN ON'), backupDone: z.literal(true) });
export const QboMirrorTurnedOn = z.object({ cleared: QboPracticeMoney });
export const QboInvoiceDifference = z.object({ invoiceId: Uuid, docNumber: z.string().nullable(), customerName: z.string(), qboBalance: z.number(), appBalance: z.number() });
// ---- the app writes its invoices to QuickBooks (decision 185)
/** A write QuickBooks refused, for a person: the invoice, why, and what to do. */
export const QboWriteFailure = z.object({
    id: Uuid,
    /** an invoice, a payment (one check, an application) or a credit (decision 186) */
    kind: z.enum(['Invoice', 'Payment', 'CreditMemo']).default('Invoice'),
    invoiceId: Uuid.nullable(),
    invoiceNo: z.number().int().nullable(),
    customerId: Uuid.nullable().default(null),
    customerName: z.string().nullable(),
    amount: z.number().nullable().default(null),
    needs: z.string().nullable(),
    error: z.string().nullable(),
    at: z.string(),
});
export const QboWriteStatus = z.object({
    connected: z.boolean(),
    /** QuickBooks' payments come back onto the app's invoices only while the money mirror is on */
    mirrorOn: z.boolean(),
    on: z.boolean(),
    /** an invoice made in the app now is real and goes to QuickBooks (on, and its first day has come) */
    real: z.boolean(),
    paused: z.boolean(),
    /** paused by the app itself, and why (QuickBooks numbered an invoice on its own) */
    pausedWhy: z.string().nullable().default(null),
    from: IsoDate.nullable(),
    turnedOnAt: z.string().nullable(),
    turnedOnByName: z.string().nullable(),
    /** turned on from another server (staging restored from production): this one never writes */
    otherServer: z.boolean(),
    /** QuickBooks must be connected again before anything more is sent */
    reconnect: z.boolean(),
    counts: z.object({ waiting: z.number().int(), sent: z.number().int(), failed: z.number().int() }),
    lastSentAt: z.string().nullable(),
    failures: z.array(QboWriteFailure),
    /** why it cannot be turned on now (null when it can) */
    blocked: z.string().nullable(),
    /** the switch day, once it has come: the app keeps the customer side's money and writes it to QuickBooks (decision 186) */
    switchedAt: z.string().nullable().default(null),
});
export const QboWriteTurnOn = z.object({ from: IsoDate });
// ---- practice money (decisions 140, 187): counted nowhere, deleted by the owner when a practice run is over
export const PracticeCounts = z.object({
    invoices: z.number().int(), invoiceTotal: z.number(), payments: z.number().int(), paymentTotal: z.number(),
    credits: z.number().int(), creditTotal: z.number(), orders: z.number().int(), ordersBack: z.number().int(),
});
export const PracticeClear = z.object({ confirm: z.literal('DELETE PRACTICE') });
// ---- the switch day (plan C12, decision 197): the app becomes the books for the customer side
export const BooksSwitchStatus = z.object({
    switchedAt: z.string().nullable(),
    blocked: z.array(z.string()),
    practice: PracticeCounts,
    ledgerFrom: IsoDate.nullable(),
    proposedFrom: IsoDate.nullable(),
    nextInvoiceNo: z.number().int().nullable(),
    outbox: z.object({ waiting: z.number().int(), failed: z.number().int() }),
    differences: z.object({ invoices: z.number().int(), amount: z.number() }),
    running: z.boolean(),
    error: z.string().nullable(),
    enteredInQuickBooks: z.object({ invoices: z.number().int(), payments: z.number().int(), credits: z.number().int(), docNumbers: z.array(z.string()) }).nullable().default(null),
    ready: z.object({
        stockCountedAt: z.string().nullable(), countedProducts: z.number().int(),
        squareConfigured: z.boolean(), squareTested: z.boolean(), squareLive: z.boolean(),
        emailConfigured: z.boolean(), emailLive: z.boolean(),
    }).default({ stockCountedAt: null, countedProducts: 0, squareConfigured: false, squareTested: false, squareLive: false, emailConfigured: false, emailLive: false }),
});
export const BooksSwitchAsk = z.object({ confirm: z.literal('SWITCH'), backupDone: z.literal(true) });
// ---- QuickBooks' Profit and Loss, read live (decision 198)
/** A day the calendar has (2026-02-31 is not one). */
const CalendarDate = IsoDate.refine((d) => {
    const t = Date.parse(`${d}T00:00:00Z`);
    return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === d;
}, 'Not a date on the calendar');
export const QboPnlAsk = z.object({ from: CalendarDate, to: CalendarDate, by: z.enum(['total', 'month']).default('total') });
export const QboPnl = z.object({
    from: IsoDate,
    to: IsoDate,
    by: z.enum(['total', 'month']),
    readAt: z.string(),
    basis: z.string().nullable(),
    columns: z.array(z.object({ title: z.string(), from: z.string().nullable(), to: z.string().nullable() })),
    sections: z.array(z.object({ group: z.string(), title: z.string(), total: z.array(z.number()), lines: z.array(z.object({ name: z.string(), values: z.array(z.number()) })) })),
});
export const QboWriteDrained = z.object({ sent: z.number().int(), failed: z.number().int(), waiting: z.number().int(), stopped: z.string().nullable() });
// ---- the weather ahead (decision 113): the National Weather Service forecast kept as facts of the day
export const WeatherDay = z.object({ day: IsoDate, highF: z.number().nullable(), lowF: z.number().nullable(), precipPct: z.number().nullable(), forecast: z.string().nullable() });
// ---- compliance, food safety and the fleet (decision 112)
export const RenewalKind = z.enum(['license', 'permit', 'insurance', 'registration', 'inspection', 'certification', 'other']);
export const RenewalCreate = z.object({
    name: z.string().trim().min(1).max(120), kind: RenewalKind, holder: z.string().trim().max(80).nullable().optional(),
    expiresOn: IsoDate, remindDays: z.number().int().min(0).max(365).default(30), note: z.string().trim().max(500).nullable().optional(),
});
/** a change sends every field: nothing left out is reset to a default or cleared by accident */
export const RenewalPut = z.object({
    name: z.string().trim().min(1).max(120), kind: RenewalKind, holder: z.string().trim().max(80).nullable(),
    expiresOn: IsoDate, remindDays: z.number().int().min(0).max(365), note: z.string().trim().max(500).nullable(),
});
export const Renewal = z.object({
    id: Uuid, name: z.string(), kind: RenewalKind, holder: z.string().nullable(), expiresOn: IsoDate, remindDays: z.number().int(), note: z.string().nullable(),
    /** days until it runs out (negative: already out); due once within its reminder days */
    daysLeft: z.number().int(), status: z.enum(['ok', 'due', 'expired']),
});
export const TempPlace = z.enum(['cooler', 'dock', 'truck']);
export const LoadCondition = z.enum(['good', 'fair', 'poor']);
export const TemperatureCreate = z.object({
    place: TempPlace, tempF: z.number().min(-40).max(140).multipleOf(0.1), siteId: Uuid.optional(), receiptId: Uuid.optional(), vehicleId: Uuid.optional(),
    condition: LoadCondition.optional(), note: z.string().trim().max(500).optional(), takenAt: z.string().datetime({ offset: true }).optional(),
});
export const Temperature = z.object({
    id: Uuid, place: TempPlace, siteName: z.string().nullable(), receiptId: Uuid.nullable(), vendorName: z.string().nullable(), vehicleName: z.string().nullable(),
    tempF: z.number(), condition: LoadCondition.nullable(), note: z.string().nullable(), takenAt: z.string(), byName: z.string().nullable(),
    /** outside the limits in force when it was taken (kept on the reading) */
    outOfRange: z.boolean(), limitMinF: z.number().nullable(), limitMaxF: z.number().nullable(),
    /** written more than an hour after it was taken */
    enteredLate: z.boolean(), createdAt: z.string(),
    /** taken back by a correction, with the reason */
    voided: z.boolean(), voidNote: z.string().nullable(),
});
export const LogVoid = z.object({ note: z.string().trim().min(1).max(500) });
export const FoodSafetySettings = z.object({
    coolerMinF: z.number().min(-40).max(140), coolerMaxF: z.number().min(-40).max(140), dockMinF: z.number().min(-40).max(140), dockMaxF: z.number().min(-40).max(140),
    truckMinF: z.number().min(-40).max(140), truckMaxF: z.number().min(-40).max(140),
});
export const VehicleCreate = z.object({ name: z.string().trim().min(1).max(40), plate: z.string().trim().max(20).nullable().optional(), note: z.string().trim().max(500).nullable().optional() });
export const VehiclePatch = VehicleCreate.partial().extend({ active: z.boolean().optional() });
export const Vehicle = z.object({
    id: Uuid, name: z.string(), plate: z.string().nullable(), note: z.string().nullable(), active: z.boolean(),
    lastOdometer: z.number().int().nullable(), lastService: IsoDate.nullable(),
    /** fuel in 30 days, in dollars: owner and office only (null for others) */
    fuel30: z.number().nullable(),
    /** its renewals (by holder name) due or expired */
    renewalsDue: z.number().int(),
});
export const VehicleLogCreate = z.object({
    kind: z.enum(['fuel', 'service', 'odometer']), odometer: z.number().int().min(0).max(5_000_000).optional(), gallons: z.number().positive().max(1000).optional(),
    amount: z.number().nonnegative().max(1_000_000).multipleOf(0.01).optional(), note: z.string().trim().max(500).optional(), loggedOn: IsoDate.optional(),
});
export const VehicleLog = z.object({
    id: Uuid, vehicleId: Uuid, kind: z.enum(['fuel', 'service', 'odometer']), odometer: z.number().int().nullable(), gallons: z.number().nullable(), amount: z.number().nullable(),
    note: z.string().nullable(), loggedOn: IsoDate, byName: z.string().nullable(),
    voided: z.boolean(), voidNote: z.string().nullable(),
});
// ---- office worklists (decision 108)
export const PromiseCreate = z.object({ amount: z.number().positive().multipleOf(0.01), promisedOn: IsoDate, note: z.string().trim().max(500).optional() });
export const PaymentPromise = z.object({
    id: Uuid, customerId: Uuid, amount: z.number(), promisedOn: IsoDate, note: z.string().nullable(), createdAt: z.string(), byName: z.string().nullable(),
    /** kept: paid at least that much from the day it was made through the day promised; broken: the day passed short */
    status: z.enum(['open', 'kept', 'broken']), paidSince: z.number(),
});
export const CollectionRow = z.object({
    customerId: Uuid, customerName: z.string(), balance: z.number(), pastDue: z.number(), oldestDue: IsoDate, daysLate: z.number().int(),
    lastPaidAt: z.string().nullable(), lastContactAt: z.string().nullable(), onHold: z.boolean(),
    promise: PaymentPromise.nullable(),
    /** put away with money after (decision 184): brought back on their page before it is collected */
    archived: z.boolean().default(false),
});
export const ComplaintRow = z.object({
    id: Uuid, customerId: Uuid, customerName: z.string(), body: z.string(), happenedAt: z.string(), doneAt: z.string().nullable(), byName: z.string().nullable(),
});
export const NoCostRow = z.object({
    itemId: Uuid, itemName: z.string(), sku: z.string().nullable(), packUnit: z.string(), lines: z.number().int(), sales: z.number(), lastSold: IsoDate,
});
// ---- the order day's vendor tabs (decision 76): what the model says, what was ordered, received and short
export const PurchaseOrderStatus = z.enum(['draft', 'sent', 'confirmed', 'received', 'closed', 'cancelled']);
export const VendorSheetLine = z.object({
    itemId: Uuid, itemName: z.string(), code: z.string().nullable(), packUnit: z.string(),
    /** what the order day's model wants from this vendor for the load, in packs (0 when it wants none) */
    model: z.number(),
    /** on the vendor's purchase order for the load; null = not on it */
    ordered: z.number().nullable(),
    /** received so far; null = nothing received yet */
    received: z.number().nullable(),
    unitCost: z.number().nullable(),
    note: z.string().nullable(),
    /** this vendor's live offer on the item (Mendoza's texted price): the line is left for the office to fill */
    offer: z.object({ price: z.number().nullable(), note: z.string().nullable() }).nullable().default(null),
    /** other vendors offering this item for the load, cheapest first */
    offeredBy: z.array(z.object({ vendorId: Uuid, vendorName: z.string(), price: z.number().nullable() })).default([]),
    /** this item on other vendors' orders for the same load (bought a bit from each): the model here is net of it */
    elsewhere: z.array(z.object({
        vendorId: Uuid, vendorName: z.string(),
        /** on that vendor's order */
        qty: z.number(),
        /** of it, still to arrive (what came is already in on hand; what a closed order did not bring is still needed) */
        coming: z.number().default(0),
    })).default([]),
});
export const VendorSheet = z.object({
    vendorId: Uuid, vendorName: z.string(),
    purchaseOrderId: Uuid.nullable(), poNo: z.string().nullable(), status: PurchaseOrderStatus.nullable(),
    placedAt: z.string().nullable(), placedBy: z.string().nullable(),
    lines: z.array(VendorSheetLine),
});
export const VendorSheets = z.object({
    load: OrderDaySetting, loadDate: IsoDate,
    /** an earlier load of this weekday with an order placed and not received yet (the truck came in, nobody closed it) */
    openEarlier: IsoDate.nullable().default(null),
    vendors: z.array(VendorSheet),
});
export const VendorSheetPut = z.object({
    date: IsoDate.optional(), load: Weekday.optional(),
    /** unitCost: a price a person gave (kept as typed); null: follow the vendor's price again; absent: as it was (decision 160) */
    lines: z.array(z.object({ itemId: Uuid, qty: z.number().nonnegative().max(100000), note: z.string().trim().max(200).optional(), unitCost: z.number().nonnegative().max(100000).nullable().optional() })).max(500),
});
export const VendorSheetAction = z.object({ date: IsoDate.optional(), load: Weekday.optional() });
/** One item put on a vendor's order for the load from the plan (decision 154): the vendor's other lines stay. */
export const VendorSheetLinePut = z.object({
    date: IsoDate.optional(), load: Weekday.optional(), itemId: Uuid, qty: z.number().nonnegative().max(100000),
    /** another vendor whose draft for the load carries the item: it comes off there in the same save, so it is never bought twice */
    takeOffVendorId: Uuid.optional(),
    /** the price this vendor gave for it (decision 160): kept as typed on the line; empty, the offer or its newest price */
    unitCost: z.number().nonnegative().max(1_000_000).optional(),
});
/** A vendor's weekly offer (decision 77): the items they texted with the price per case. */
export const VendorOffer = z.object({
    vendorId: Uuid, vendorName: z.string(),
    enteredAt: z.string().nullable(), enteredBy: z.string().nullable(),
    /** an order of this vendor was placed with the offer on: it drops off 36 hours after */
    usedAt: z.string().nullable(),
    /** each line is good through the end of the day after it was offered */
    lines: z.array(z.object({ itemId: Uuid, itemName: z.string(), packUnit: z.string(), price: z.number().nullable(), note: z.string().nullable(), offeredOn: IsoDate, goodThrough: IsoDate })),
});
export const VendorOfferPut = z.object({
    lines: z.array(z.object({ itemId: Uuid, price: z.number().nonnegative().max(100000).nullable(), note: z.string().trim().max(200).optional() })).max(300),
});
// ---- vendor prices (decision 79): every price any vendor gave, from a text offer, a price list, a bill or the dock
export const VendorPriceSource = z.enum(['offer', 'list', 'invoice', 'receipt', 'manual', 'import']);
export const VendorPriceRow = z.object({
    date: IsoDate, vendorId: Uuid, vendorName: z.string(), itemId: Uuid, itemName: z.string(), packUnit: z.string(),
    /** the vendor's price per unit as they gave it (freight not included) */
    price: z.number(), unit: z.string().nullable(), source: VendorPriceSource,
});
export const VendorPriceLatest = z.object({
    itemId: Uuid, itemName: z.string(), packUnit: z.string(),
    /** the latest price from each vendor that ever gave one for the item */
    vendors: z.array(z.object({
        vendorId: Uuid, price: z.number(), date: IsoDate, source: VendorPriceSource,
        /** that vendor's newest list price for it, the list price before it (same unit) and which way it moved (decision 194) */
        listPrice: z.number().nullable().default(null), previous: z.number().nullable().default(null), change: z.enum(['up', 'down', 'same']).nullable().default(null),
        /** older than that vendor's newest price list: the list did not price it (not on it, or its code not linked) */
        stale: z.boolean().default(false),
    })),
    /** the vendor with the lowest of those latest prices (ties: the most recent) */
    cheapestVendorId: Uuid.nullable(),
});
export const VendorPrices = z.object({
    /** each vendor with its newest price list's date and how many lines on it name no product yet (decision 194) */
    vendors: z.array(z.object({ id: Uuid, name: z.string(), listDate: IsoDate.nullable().default(null), unlinkedOnList: z.number().int().default(0) })),
    latest: z.array(VendorPriceLatest),
    rows: z.array(VendorPriceRow),
    /** more rows than shown: narrow the period, the vendor or the item */
    truncated: z.boolean(),
});
export const OrderHistoryRow = z.object({
    purchaseOrderId: Uuid, poNo: z.string().nullable(), status: PurchaseOrderStatus,
    placedAt: z.string().nullable(), placedBy: z.string().nullable(), loadDate: IsoDate.nullable(), loadWeekday: Weekday.nullable(),
    vendorId: Uuid, vendorName: z.string(), itemId: Uuid.nullable(), itemName: z.string().nullable(), code: z.string().nullable(),
    ordered: z.number(), received: z.number().nullable(),
    /** ordered minus received once the order is received; null while it is still on its way */
    short: z.number().nullable(),
    unitCost: z.number().nullable(), total: z.number().nullable(), note: z.string().nullable(),
});
/** One invoice "Invoice the accounts" would make (decision 175): the order and its lines as they stand, or why not. */
export const OrderDayPreview = z.object({
    customerId: Uuid, customerName: z.string(), orderId: Uuid, orderNo: z.number().int(), problem: z.string().nullable(),
    /** the order as the screen saw it: sent back with the invoice, a change made meanwhile is caught */
    version: z.string(),
    /** picked or further along: its lines are invoiced as they are (change the invoice afterwards, decision 170) */
    locked: z.boolean().default(false),
    lines: z.array(z.object({
        lineNo: z.number().int(), itemId: Uuid, itemName: z.string(), qty: z.number(), unit: UnitCode, unitPrice: Money.nullable(), typed: z.boolean(),
        /** the vendors short of it for this load (decision 176): Mata's, 2 of 10, from the invoice; a person types the new number */
        vendorShorts: z.array(z.object({ vendor: z.string(), coming: z.number(), ordered: z.number(), source: z.enum(['invoice', 'typed', 'dock']) })).default([]),
    })),
});
export const OrderDayInvoiceResult = z.object({ customerId: Uuid, customerName: z.string(), orderNo: z.number().int(), invoice: Invoice.nullable(), problem: z.string().nullable() });
// Vendor shorts (decision 176): what a vendor will not bring for a load's order, from its invoice, what it said (typed)
// or the dock, with the stores' orders it reaches. The app flags and reminds; a person changes the orders.
export const VendorShortSource = z.enum(['invoice', 'typed', 'dock']);
export const VendorShortStore = z.object({
    customerId: Uuid, customerName: z.string(), orderId: Uuid, orderNo: z.number().int(), orderStatus: OrderStatus, requestedDate: IsoDate,
    qty: z.number(), invoiceId: Uuid.nullable(), invoiceNo: z.number().int().nullable(),
    /** the text to send the store, in its language (the supplier, never the vendor's name) */
    message: z.string(),
    /** the morning to-do on Follow-ups */
    taskId: Uuid.nullable(), taskDone: z.boolean(),
});
export const VendorShort = z.object({
    id: Uuid, purchaseOrderId: Uuid, vendorId: Uuid, vendorName: z.string(), loadDate: IsoDate, loadWeekday: z.number().int().nullable(),
    itemId: Uuid, itemName: z.string(), unit: z.string().nullable(),
    ordered: z.number(), coming: z.number(), source: VendorShortSource,
    invoiceQty: z.number().nullable(), typedQty: z.number().nullable(), dockQty: z.number().nullable(),
    /** the vendor's invoice it was read from (a paper from the mailbox): open it to check before telling the stores */
    documentId: Uuid.nullable(),
    foundAt: z.string().nullable(), onHand: z.number(), doneAt: z.string().nullable(),
    stores: z.array(VendorShortStore),
});
/** What the vendor said is coming (a call or a text), typed on the load's order; null takes it back. */
export const VendorShortTyped = z.object({ vendorId: Uuid, date: IsoDate.optional(), load: Weekday.optional(), itemId: Uuid, qty: z.number().nonnegative().max(100000).nullable() });
// ---- problem shape for every error response
// Stock & Buy, slice 1: the cooler count. A count never overwrites stock on hand: it is recorded, and the
// difference against the ledger becomes an 'adjust' movement (so the drift is kept and can be measured).
export const CountSheetRow = z.object({
    itemId: Uuid,
    itemName: z.string(),
    packUnit: UnitCode,
    content: z.number().nullable(),
    contentUnit: ContentUnit.nullable(),
    sellLoose: z.boolean(),
    onHandPacks: z.number(),
    lastCountedPacks: z.number().nullable(),
    lastCountedAt: z.string().nullable(),
});
export const CountSheet = z.object({ locationId: Uuid, locationName: z.string(), rows: z.array(CountSheetRow) });
/** One item counted: whole stock units, plus what is left in the open one when the item is broken. */
export const CountLine = z.object({
    itemId: Uuid,
    // no cooler holds more; a larger number (a scanned barcode) would not fit the ledger and is refused here, not by the database
    sealed: z.number().int().nonnegative().max(99999),
    open: z.object({ remaining: z.number().finite().nonnegative(), unit: OpenRemainingUnit }).optional(),
    note: z.string().max(200).optional(),
});
export const CountCreate = z.object({
    locationId: Uuid.optional(),
    siteId: Uuid.optional(), // when no locationId: the site whose cooler is counted (default: the hub)
    countedAt: z.string().datetime({ offset: true }).optional(),
    lines: z.array(CountLine).min(1).max(500),
});
export const CountResultLine = z.object({
    itemId: Uuid,
    itemName: z.string(),
    countedPacks: z.number(),
    systemPacks: z.number(),
    adjustPacks: z.number(),
    adjustSealed: z.number(),
    adjustOpen: z.number(),
    flagged: z.boolean(), // drift beyond the open-unit tolerance (or any sealed drift)
});
export const CountResult = z.object({
    locationId: Uuid,
    locationName: z.string(),
    countedAt: z.string(),
    counted: z.number().int(),
    adjusted: z.number().int(),
    lines: z.array(CountResultLine),
});
export const Problem = z.object({ status: z.number().int(), title: z.string(), detail: z.string().optional(), errors: z.array(z.object({ path: z.string(), message: z.string() })).optional() });
export * from './pricing.js';
// Translations the phone app ships with. Every user-facing string lives here so EN and ES stay in step.
export const messages = {
    en: {
        appName: 'Morales', signIn: 'Sign in', phone: 'Phone', sendCode: 'Send code', code: 'Code', verify: 'Verify',
        home: 'Home', items: 'Items', customers: 'Customers', orders: 'Orders', stock: 'Stock', signOut: 'Sign out',
        search: 'Search', cost: 'Cost', price: 'Price', noCost: 'no cost on file', balance: 'Balance', newOrder: 'New order',
        customer: 'Customer', deliveryDate: 'Delivery date', addLine: 'Add line', qty: 'Qty', total: 'Total', save: 'Save',
        cancel: 'Cancel', saved: 'Saved', error: 'Something went wrong', offline: 'Offline: changes will send when back online',
        language: 'Language', today: 'Today', callAhead: 'Call ahead', split: 'split', balancePending: 'payments not synced yet',
    },
    es: {
        appName: 'Morales', signIn: 'Entrar', phone: 'Teléfono', sendCode: 'Enviar código', code: 'Código', verify: 'Verificar',
        home: 'Inicio', items: 'Productos', customers: 'Clientes', orders: 'Pedidos', stock: 'Inventario', signOut: 'Salir',
        search: 'Buscar', cost: 'Costo', price: 'Precio', noCost: 'sin costo', balance: 'Saldo', newOrder: 'Nuevo pedido',
        customer: 'Cliente', deliveryDate: 'Fecha de entrega', addLine: 'Agregar línea', qty: 'Cant.', total: 'Total', save: 'Guardar',
        cancel: 'Cancelar', saved: 'Guardado', error: 'Algo salió mal', offline: 'Sin conexión: los cambios se enviarán al reconectar',
        language: 'Idioma', today: 'Hoy', callAhead: 'Llama antes', split: 'mitad', balancePending: 'pagos aún no sincronizados',
    },
};
// ── The Claude button (decision 137) ────────────────────────────────────────────────────────────────
/** What the person said (spoken or typed), with the last few turns so "and two more limes" is understood. */
export const AssistantAsk = z.object({
    messages: z.array(z.object({ role: z.enum(['user', 'assistant']), text: z.string().min(1).max(4000) })).min(1).max(20),
    /** the screen the person is on (a path), so "this customer" can be read */
    screen: z.string().max(200).optional(),
});
/** A screen Claude filled for a person to check and save: never saved by Claude. */
export const AssistantAction = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('invoice'),
        invoices: z.array(z.object({
            customerId: Uuid,
            customerName: z.string(),
            notes: z.string().optional(),
            lines: z.array(z.object({ itemId: Uuid, itemName: z.string(), qty: z.number().positive(), unit: z.string(), rate: z.number().nonnegative().nullable() })).min(1),
        })).min(1).max(6),
    }),
    z.object({
        kind: z.literal('count'),
        lines: z.array(z.object({ itemId: Uuid, itemName: z.string(), count: z.number().nonnegative() })).min(1),
    }),
]);
export const AssistantAnswer = z.object({
    reply: z.string(),
    action: AssistantAction.nullable(),
    /** the tools Claude used, in order (shown small under the answer) */
    used: z.array(z.string()),
});
/** Paste a call transcript or a text (decision 142): Claude fills the invoice screen; the words go on the customer's history. */
export const AssistantPaste = z.object({
    text: z.string().trim().min(1).max(20000),
    kind: z.enum(['call', 'text']).default('call'),
    /** when the person already knows who it is from */
    customerId: Uuid.optional(),
});
export const AssistantPasteAnswer = AssistantAnswer.extend({ savedTo: z.object({ customerId: Uuid, customerName: z.string() }).nullable() });
export const AssistantStatus = z.object({ configured: z.boolean() });
// ── Product details (decision 138): the box, where it is sourced by season, seasonality from our own numbers ─────
export const ItemSource = z.object({
    id: Uuid.optional(),
    region: z.string().trim().min(1).max(80),
    /** the months it comes from there, 1 = January */
    months: z.array(z.number().int().min(1).max(12)).min(1).max(12),
    vendorId: Uuid.nullable().default(null),
    vendorName: z.string().nullable().optional(),
    note: z.string().trim().max(200).nullable().default(null),
});
export const ItemProfile = z.object({
    itemId: Uuid,
    boxWeightLb: z.number().nullable(),
    boxCount: z.number().int().nullable(),
    sources: z.array(ItemSource),
    /** every vendor that gave a price for it: the latest price and how often (owner/office see prices) */
    vendors: z.array(z.object({ vendorId: Uuid, vendorName: z.string(), lastPrice: z.number().nullable(), lastDate: IsoDate, unit: z.string().nullable(), prices: z.number().int() })),
    /** by calendar month over all our history: what sold a year on average, at what price and cost */
    seasonality: z.array(z.object({
        month: z.number().int().min(1).max(12),
        years: z.number().int(),
        soldPerYear: z.number(),
        retailPerYear: z.number(),
        avgPrice: z.number().nullable(),
        avgCost: z.number().nullable(),
    })),
});
export const ItemProfilePut = z.object({
    boxWeightLb: z.number().positive().max(9999).nullable(),
    boxCount: z.number().int().positive().max(99999).nullable(),
    sources: z.array(ItemSource).max(12),
});
//# sourceMappingURL=index.js.map